import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { Capacitor } from '@capacitor/core';

// On native builds the webview is served from capacitor://localhost, so
// relative /api paths must be rewritten to the production backend.
const API_BASE = Capacitor.isNativePlatform() ? 'https://www.repscan.app' : '';
const apiUrl = (path) => `${API_BASE}${path}`;

// Auth helpers
function getSessionToken() {
  return localStorage.getItem('hcf_session_token');
}

function setSessionToken(token) {
  localStorage.setItem('hcf_session_token', token);
}

function clearSessionToken() {
  localStorage.removeItem('hcf_session_token');
}

function decodeToken(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (payload.exp && payload.exp * 1000 < Date.now()) return null;
    return payload;
  } catch {
    return null;
  }
}

// --- SVG Icons (as components for reusability elsewhere) ---
const IconCamera = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"></path>
    <circle cx="12" cy="13" r="3"></circle>
  </svg>
);
const IconHome = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
    <polyline points="9 22 9 12 15 12 15 22"></polyline>
  </svg>
);
const IconBarChart2 = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <line x1="18" y1="20" x2="18" y2="10"></line>
    <line x1="12" y1="20" x2="12" y2="4"></line>
    <line x1="6" y1="20" x2="6" y2="14"></line>
  </svg>
);
const IconHistory = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
    <path d="M3 3v5h5"></path>
    <path d="M12 7v5l4 2"></path>
  </svg>
);
const IconX = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);
const IconRepeat = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M17 2.1l4 4-4 4"></path>
    <path d="M3 12.6v-2.6a4 4 0 0 1 4-4h14"></path>
    <path d="M7 21.9l-4-4 4-4"></path>
    <path d="M21 11.4v2.6a4 4 0 0 1-4 4H3"></path>
  </svg>
);
const IconWeight = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <circle cx="12" cy="5" r="3"></circle>
    <path d="M6.5 8.5c-1 5.5 2.5 10.5 8 11.5"></path>
    <path d="M17.5 8.5c1 5.5-2.5 10.5-8 11.5"></path>
    <path d="M14 17h-4"></path>
    <path d="M15 14h-6"></path>
  </svg>
);
const IconDumbbell = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M14.4 14.4 9.6 9.6"></path>
    <path d="M18.657 5.343a2 2 0 1 0-2.828-2.828l-1.414 1.414 2.828 2.828 1.414-1.414z"></path>
    <path d="m5.343 18.657a2 2 0 1 0 2.828 2.828l1.414-1.414-2.828-2.828-1.414 1.414z"></path>
    <path d="m12 12-2-2"></path>
    <path d="m14 14-2-2"></path>
    <path d="M7.8 16.2 4.2 19.8"></path>
    <path d="M19.8 4.2 16.2 7.8"></path>
  </svg>
);
const IconTrash2 = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M3 6h18"></path>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
    <line x1="10" y1="11" x2="10" y2="17"></line>
    <line x1="14" y1="11" x2="14" y2="17"></line>
  </svg>
);
const IconEdit = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
  </svg>
);
const IconCopy = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
  </svg>
);
const IconSearch = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <circle cx="11" cy="11" r="8"></circle>
    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
  </svg>
);
const IconUpload = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
    <polyline points="17 8 12 3 7 8"></polyline>
    <line x1="12" y1="3" x2="12" y2="15"></line>
  </svg>
);
const IconZoomIn = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
        <circle cx="11" cy="11" r="8"></circle>
        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
        <line x1="11" y1="8" x2="11" y2="14"></line>
        <line x1="8" y1="11" x2="14" y2="11"></line>
    </svg>
);
const IconZoomOut = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
        <circle cx="11" cy="11" r="8"></circle>
        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
        <line x1="8" y1="11" x2="14" y2="11"></line>
    </svg>
);
const IconRefreshCw = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
        <polyline points="23 4 23 10 17 10"></polyline>
        <polyline points="1 20 1 14 7 14"></polyline>
        <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
    </svg>
);
const IconUser = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
        <circle cx="12" cy="7" r="4"></circle>
    </svg>
);
const IconSettings = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
        <circle cx="12" cy="12" r="3"></circle>
        <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"></path>
    </svg>
);
const IconDownload = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
        <polyline points="7 10 12 15 17 10"></polyline>
        <line x1="12" y1="15" x2="12" y2="3"></line>
    </svg>
);
const IconLogOut = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
        <polyline points="16 17 21 12 16 7"></polyline>
        <line x1="21" y1="12" x2="9" y2="12"></line>
    </svg>
);

const IconShield = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
    </svg>
);
const IconMessage = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
    </svg>
);

// --- Settings Storage ---
const SETTINGS_STORAGE_KEY = 'hcf-settings';
const defaultSettings = {
    weightUnit: 'lbs',
    theme: 'dark',
    dateFormat: 'MM/DD'
};

// --- Theme Configuration ---
const THEME_CONFIG = {
    dark: {
        pageBg: 'bg-gray-900', outerBg: 'bg-gray-800',
        cardBg: 'bg-gray-800', inputBg: 'bg-gray-700',
        textPrimary: 'text-white', textSecondary: 'text-gray-300',
        textMuted: 'text-gray-400', textFaint: 'text-gray-500',
        border: 'border-gray-700', borderLight: 'border-gray-800',
        hoverBg: 'hover:bg-gray-700', hoverBgLight: 'hover:bg-gray-600',
        accentBg: 'bg-red-500', accentText: 'text-red-400', accentHover: 'hover:bg-red-600',
        accentBgSoft: 'bg-red-500/20',
        navBg: 'bg-gray-800', navBorder: 'border-gray-700', navActive: 'text-red-500',
        chartAccent: '#F56565', chartSecondary: '#10B981',
        ringColor: 'ring-blue-500',
        selectedDayBg: 'bg-red-500', dotColor: 'bg-red-500',
        label: 'Dark', swatch: 'bg-gray-800',
    },
    light: {
        pageBg: 'bg-white', outerBg: 'bg-gray-100',
        cardBg: 'bg-gray-100', inputBg: 'bg-gray-100',
        textPrimary: 'text-gray-900', textSecondary: 'text-gray-700',
        textMuted: 'text-gray-500', textFaint: 'text-gray-600',
        border: 'border-gray-200', borderLight: 'border-gray-200',
        hoverBg: 'hover:bg-gray-100', hoverBgLight: 'hover:bg-gray-200',
        accentBg: 'bg-red-500', accentText: 'text-red-500', accentHover: 'hover:bg-red-600',
        accentBgSoft: 'bg-red-500/20',
        navBg: 'bg-white', navBorder: 'border-gray-200', navActive: 'text-red-500',
        chartAccent: '#EF4444', chartSecondary: '#10B981',
        ringColor: 'ring-blue-500',
        selectedDayBg: 'bg-red-500', dotColor: 'bg-red-500',
        label: 'Light', swatch: 'bg-gray-100 border border-gray-300',
    },
    rose: {
        pageBg: 'bg-pink-50', outerBg: 'bg-pink-100',
        cardBg: 'bg-pink-100', inputBg: 'bg-pink-200',
        textPrimary: 'text-pink-950', textSecondary: 'text-pink-800',
        textMuted: 'text-pink-500', textFaint: 'text-pink-400',
        border: 'border-pink-200', borderLight: 'border-pink-100',
        hoverBg: 'hover:bg-pink-200', hoverBgLight: 'hover:bg-pink-300',
        accentBg: 'bg-pink-500', accentText: 'text-pink-600', accentHover: 'hover:bg-pink-600',
        accentBgSoft: 'bg-pink-500/20',
        navBg: 'bg-pink-50', navBorder: 'border-pink-200', navActive: 'text-pink-600',
        chartAccent: '#EC4899', chartSecondary: '#F472B6',
        ringColor: 'ring-pink-400',
        selectedDayBg: 'bg-pink-500', dotColor: 'bg-pink-500',
        label: 'Rose', swatch: 'bg-pink-300',
    },
    midnight: {
        pageBg: 'bg-slate-900', outerBg: 'bg-slate-800',
        cardBg: 'bg-slate-800', inputBg: 'bg-slate-700',
        textPrimary: 'text-white', textSecondary: 'text-slate-300',
        textMuted: 'text-slate-400', textFaint: 'text-slate-500',
        border: 'border-slate-700', borderLight: 'border-slate-800',
        hoverBg: 'hover:bg-slate-700', hoverBgLight: 'hover:bg-slate-600',
        accentBg: 'bg-indigo-500', accentText: 'text-indigo-400', accentHover: 'hover:bg-indigo-600',
        accentBgSoft: 'bg-indigo-500/20',
        navBg: 'bg-slate-900', navBorder: 'border-slate-700', navActive: 'text-indigo-400',
        chartAccent: '#818CF8', chartSecondary: '#34D399',
        ringColor: 'ring-indigo-500',
        selectedDayBg: 'bg-indigo-500', dotColor: 'bg-indigo-500',
        label: 'Midnight', swatch: 'bg-slate-700',
    },
    neon: {
        pageBg: 'bg-zinc-950', outerBg: 'bg-zinc-900',
        cardBg: 'bg-zinc-900', inputBg: 'bg-zinc-800',
        textPrimary: 'text-white', textSecondary: 'text-zinc-300',
        textMuted: 'text-zinc-400', textFaint: 'text-zinc-500',
        border: 'border-zinc-700', borderLight: 'border-zinc-800',
        hoverBg: 'hover:bg-zinc-800', hoverBgLight: 'hover:bg-zinc-700',
        accentBg: 'bg-cyan-500', accentText: 'text-cyan-400', accentHover: 'hover:bg-cyan-600',
        accentBgSoft: 'bg-cyan-400/20',
        navBg: 'bg-zinc-950', navBorder: 'border-zinc-700', navActive: 'text-cyan-400',
        chartAccent: '#22D3EE', chartSecondary: '#4ADE80',
        ringColor: 'ring-cyan-500',
        selectedDayBg: 'bg-cyan-500', dotColor: 'bg-cyan-500',
        label: 'Neon', swatch: 'bg-zinc-800',
    },
};

const getTheme = (name) => THEME_CONFIG[name] || THEME_CONFIG.dark;

// --- Settings Screen Component ---
function SettingsScreen({ isOpen, onClose, settings, onUpdateSettings }) {
    if (!isOpen) return null;

    const theme = getTheme(settings?.theme);

    const weightOptions = [
        { value: 'lbs', label: 'Pounds (lbs)' },
        { value: 'kg', label: 'Kilograms (kg)' }
    ];

    const dateFormatOptions = [
        { value: 'MM/DD', label: 'MM/DD/YYYY' },
        { value: 'DD/MM', label: 'DD/MM/YYYY' }
    ];

    const SettingRow = ({ label, value, options, onChange }) => (
        <div className={`flex items-center justify-between py-3 border-b ${theme.border}`}>
            <span className={theme.textSecondary}>{label}</span>
            <div className="flex gap-1">
                {options.map(opt => (
                    <button
                        key={opt.value}
                        onClick={() => onChange(opt.value)}
                        className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                            value === opt.value
                                ? `${theme.accentBg} text-white`
                                : `${theme.inputBg} ${theme.textSecondary} ${theme.hoverBgLight}`
                        }`}
                    >
                        {opt.label}
                    </button>
                ))}
            </div>
        </div>
    );

    return (
        <>
            {/* Backdrop */}
            <div className="fixed inset-0 bg-black/50 z-50" onClick={onClose} />
            {/* Settings Panel */}
            <div className={`fixed inset-x-4 top-1/2 -translate-y-1/2 rounded-xl z-50 shadow-xl max-w-md mx-auto ${theme.cardBg} ${theme.textPrimary}`}>
                <div className={`p-4 border-b ${theme.border}`}>
                    <div className="flex justify-between items-center">
                        <h2 className="text-xl font-bold">Settings</h2>
                        <button onClick={onClose} className={`p-2 rounded-full ${theme.hoverBg}`}>
                            <IconX className="w-5 h-5" />
                        </button>
                    </div>
                </div>
                <div className="p-4">
                    <h3 className={`text-sm font-semibold ${theme.textMuted} uppercase tracking-wide mb-2`}>Display</h3>
                    <SettingRow
                        label="Weight Unit"
                        value={settings.weightUnit}
                        options={weightOptions}
                        onChange={(val) => onUpdateSettings({ ...settings, weightUnit: val })}
                    />
                    <div className={`py-3 border-b ${theme.border}`}>
                        <span className={`${theme.textSecondary} block mb-2`}>Theme</span>
                        <div className="grid grid-cols-5 gap-2">
                            {Object.entries(THEME_CONFIG).map(([key, t]) => (
                                <button
                                    key={key}
                                    onClick={() => onUpdateSettings({ ...settings, theme: key })}
                                    className={`flex flex-col items-center gap-1 p-2 rounded-lg transition-colors ${
                                        settings.theme === key ? `ring-2 ${theme.ringColor}` : ''
                                    }`}
                                >
                                    <div className={`w-8 h-8 rounded-full ${t.swatch}`} />
                                    <span className={`text-xs ${theme.textMuted}`}>{t.label}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                    <SettingRow
                        label="Date Format"
                        value={settings.dateFormat}
                        options={dateFormatOptions}
                        onChange={(val) => onUpdateSettings({ ...settings, dateFormat: val })}
                    />
                </div>
            </div>
        </>
    );
}

// --- Admin Screen ---
const ADMIN_EMAILS = ['crettew@gmail.com', 'guppibot@gmail.com'];

function AdminScreen({ isOpen, onClose, settings, authFetch }) {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const theme = getTheme(settings?.theme);

    useEffect(() => {
        if (!isOpen) return;
        setLoading(true);
        setError('');
        authFetch('/api/admin/stats')
            .then(res => {
                if (!res.ok) throw new Error('Failed to load');
                return res.json();
            })
            .then(data => setStats(data))
            .catch(() => setError('Failed to load admin stats'))
            .finally(() => setLoading(false));
    }, [isOpen, authFetch]);

    if (!isOpen) return null;

    const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

    return (
        <>
            <div className="fixed inset-0 bg-black/50 z-50" onClick={onClose} />
            <div className={`fixed inset-x-4 top-8 bottom-8 rounded-xl z-50 shadow-xl max-w-md mx-auto flex flex-col ${theme.cardBg} ${theme.textPrimary}`}>
                <div className={`p-4 border-b ${theme.border} flex-shrink-0`}>
                    <div className="flex justify-between items-center">
                        <h2 className="text-xl font-bold">Admin</h2>
                        <button onClick={onClose} className={`p-2 rounded-full ${theme.hoverBg}`}>
                            <IconX className="w-5 h-5" />
                        </button>
                    </div>
                </div>
                <div className="flex-1 overflow-y-auto p-4">
                    {loading && <p className={theme.textMuted}>Loading...</p>}
                    {error && <p className="text-red-400">{error}</p>}
                    {stats && (
                        <>
                            <div className={`text-center mb-4 p-3 rounded-lg ${theme.inputBg}`}>
                                <p className={`text-3xl font-bold ${theme.accentText}`}>{stats.totalUsers}</p>
                                <p className={`text-sm ${theme.textMuted}`}>Total Users</p>
                            </div>
                            <div className="space-y-3">
                                {stats.users.map((u, i) => (
                                    <div key={i} className={`p-3 rounded-lg ${theme.inputBg}`}>
                                        <div className="flex justify-between items-start mb-2">
                                            <p className={`font-medium text-sm ${theme.textPrimary}`}>{u.email}</p>
                                            <span className={`text-xs ${theme.textMuted}`}>Joined {fmtDate(u.signedUp)}</span>
                                        </div>
                                        <div className="grid grid-cols-5 gap-1 text-center">
                                            <div>
                                                <p className={`text-lg font-bold ${theme.textPrimary}`}>{u.days7d}</p>
                                                <p className={`text-xs ${theme.textMuted}`}>7d days</p>
                                            </div>
                                            <div>
                                                <p className={`text-lg font-bold ${theme.textPrimary}`}>{u.days30d}</p>
                                                <p className={`text-xs ${theme.textMuted}`}>30d days</p>
                                            </div>
                                            <div>
                                                <p className={`text-lg font-bold ${theme.textPrimary}`}>{u.daysTotal}</p>
                                                <p className={`text-xs ${theme.textMuted}`}>All days</p>
                                            </div>
                                            <div>
                                                <p className={`text-lg font-bold ${theme.textPrimary}`}>{u.exercises30d}</p>
                                                <p className={`text-xs ${theme.textMuted}`}>30d exs</p>
                                            </div>
                                            <div>
                                                <p className={`text-lg font-bold ${theme.textPrimary}`}>{u.exercisesTotal}</p>
                                                <p className={`text-xs ${theme.textMuted}`}>All exs</p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </>
    );
}

// --- Profile Menu Component ---
function ProfileMenu({ isOpen, onClose, workouts, onOpenSettings, onOpenAdmin, settings, email, onSignOut }) {
    const theme = getTheme(settings?.theme);
    const [feedbackOpen, setFeedbackOpen] = useState(false);
    const [feedbackMsg, setFeedbackMsg] = useState('');
    const [feedbackStatus, setFeedbackStatus] = useState(null); // 'sending' | 'sent' | 'error'
    if (!isOpen) return null;

    const exportToCSV = async () => {
        // Generate CSV content
        const headers = ['Date', 'Exercise', 'Reps', 'Weight (lbs)', 'Seconds', 'Sets', 'Difficulty', 'Station'];
        const difficultyLabels = { 1: 'Easy', 2: 'Moderate', 3: 'Hard', 4: 'Brutal' };

        const rows = workouts.flatMap(workout => {
            const dateStr = workout.date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
            });
            return workout.exercises.map(ex => [
                dateStr,
                `"${ex.name}"`,
                ex.reps || '',
                ex.weight || '',
                ex.seconds || '',
                ex.rounds || 1,
                difficultyLabels[ex.difficulty] || '',
                ex.station || ''
            ].join(','));
        });

        const csvContent = [headers.join(','), ...rows].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const fileName = `hcf-workouts-${new Date().toISOString().split('T')[0]}.csv`;

        // Try Web Share API first (works great on mobile for email/share)
        if (navigator.share && navigator.canShare) {
            const file = new File([blob], fileName, { type: 'text/csv' });
            if (navigator.canShare({ files: [file] })) {
                try {
                    await navigator.share({
                        files: [file],
                        title: 'HCF Workout Data',
                        text: 'My workout data from HCF Tracker'
                    });
                    return;
                } catch (err) {
                    if (err.name !== 'AbortError') {
                        console.log('Share failed, falling back to download');
                    } else {
                        return; // User cancelled
                    }
                }
            }
        }

        // Fallback: download the file
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const handleSendFeedback = async () => {
        if (!feedbackMsg.trim()) return;
        setFeedbackStatus('sending');
        try {
            const token = getSessionToken();
            const res = await fetch(apiUrl('/api/feedback'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ message: feedbackMsg.trim() }),
            });
            if (!res.ok) throw new Error();
            setFeedbackStatus('sent');
            setFeedbackMsg('');
            setTimeout(() => { setFeedbackOpen(false); setFeedbackStatus(null); }, 1500);
        } catch {
            setFeedbackStatus('error');
        }
    };

    const isAdmin = ADMIN_EMAILS.includes(email);
    const menuItems = [
        { icon: IconSettings, label: 'Settings', onClick: onOpenSettings },
        ...(isAdmin ? [{ icon: IconShield, label: 'Admin', onClick: onOpenAdmin }] : []),
        { icon: IconMessage, label: 'Send Feedback', onClick: () => setFeedbackOpen(true), stayOpen: true },
        { icon: IconDownload, label: 'Export Data', onClick: exportToCSV },
        { icon: IconLogOut, label: 'Log Out', onClick: onSignOut },
    ];

    return (
        <>
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black/50 z-40"
                onClick={onClose}
            />
            {/* Slide-out menu */}
            <div className={`fixed top-0 right-0 h-full w-64 z-50 shadow-xl transform transition-transform duration-300 ease-out ${theme.cardBg} ${theme.textPrimary}`}>
                <div className={`p-4 border-b ${theme.border}`}>
                    <div className="flex justify-between items-center">
                        <h2 className="text-xl font-bold">Profile</h2>
                        <button onClick={onClose} className={`p-2 rounded-full ${theme.hoverBg}`}>
                            <IconX className="w-5 h-5" />
                        </button>
                    </div>
                </div>
                <div className="p-4">
                    <div className={`flex items-center gap-3 mb-6 pb-4 border-b ${theme.border}`}>
                        <div className={`w-12 h-12 ${theme.accentBg} rounded-full flex items-center justify-center text-white text-lg font-bold`}>
                            {email ? email[0].toUpperCase() : '?'}
                        </div>
                        <div>
                            <p className={`text-sm ${theme.textMuted}`}>{email}</p>
                        </div>
                    </div>
                    <nav className="space-y-2">
                        {menuItems.map((item) => (
                            <button
                                key={item.label}
                                onClick={() => { item.onClick(); if (!item.stayOpen) onClose(); }}
                                className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors text-left ${theme.hoverBg}`}
                            >
                                <item.icon className={`w-5 h-5 ${theme.textMuted}`} />
                                <span>{item.label}</span>
                            </button>
                        ))}
                    </nav>
                    <div className={`mt-6 pt-4 border-t ${theme.border} text-center`}>
                        <a
                            href="https://venmo.com/craigrettew"
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`text-sm ${theme.textMuted} hover:text-blue-400 transition-colors`}
                        >
                            🥤 Buy me a protein shake
                        </a>
                    </div>
                </div>
            </div>
            {/* Feedback Modal */}
            {feedbackOpen && (
                <>
                    <div className="fixed inset-0 bg-black/60 z-50" onClick={() => { setFeedbackOpen(false); setFeedbackStatus(null); }} />
                    <div className={`fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-80 ${theme.cardBg} rounded-xl p-5 shadow-xl`}>
                        <h3 className={`text-lg font-bold ${theme.textPrimary} mb-3`}>Send Feedback</h3>
                        {feedbackStatus === 'sent' ? (
                            <p className="text-green-400 text-sm text-center py-4">Thanks for your feedback!</p>
                        ) : (
                            <>
                                <textarea
                                    value={feedbackMsg}
                                    onChange={(e) => setFeedbackMsg(e.target.value)}
                                    placeholder="Let me know what you think."
                                    rows={4}
                                    className={`w-full ${theme.inputBg} ${theme.textPrimary} rounded-lg p-3 text-sm border ${theme.border} focus:outline-none focus:border-red-500 resize-none`}
                                />
                                {feedbackStatus === 'error' && <p className="text-red-400 text-xs mt-1">Failed to send. Try again.</p>}
                                <div className="flex gap-2 mt-3">
                                    <button
                                        onClick={() => { setFeedbackOpen(false); setFeedbackStatus(null); }}
                                        className={`flex-1 py-2 rounded-lg text-sm ${theme.textMuted} ${theme.hoverBg}`}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleSendFeedback}
                                        disabled={feedbackStatus === 'sending' || !feedbackMsg.trim()}
                                        className={`flex-1 py-2 rounded-lg text-sm font-semibold text-white ${!feedbackMsg.trim() || feedbackStatus === 'sending' ? 'bg-gray-600' : 'bg-red-500 hover:bg-red-600'} transition-colors`}
                                    >
                                        {feedbackStatus === 'sending' ? 'Sending...' : 'Send'}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </>
            )}
        </>
    );
}

// --- Landing Page (unauthenticated) ---
function LandingPage() {
    const [email, setEmail] = useState('');
    const [sent, setSent] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [cooldown, setCooldown] = useState(0);

    useEffect(() => {
        if (cooldown <= 0) return;
        const timer = setTimeout(() => setCooldown(c => c - 1), 1000);
        return () => clearTimeout(timer);
    }, [cooldown]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const res = await fetch(apiUrl('/api/auth/login'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: email.trim() }),
            });
            const data = await res.json();
            if (!res.ok) {
                if (res.status === 429) {
                    setCooldown(60);
                }
                throw new Error(data.error || 'Failed to send link');
            }
            setSent(true);
            setCooldown(60);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const signUpForm = (
        <div className="w-full max-w-sm">
            {!sent ? (
                <form onSubmit={handleSubmit} className="space-y-3">
                    <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter your email"
                        required
                        className="w-full px-4 py-3 rounded-lg bg-gray-700 text-white border border-gray-600 focus:outline-none focus:border-red-500"
                    />
                    {error && <p className="text-red-400 text-sm">{error}</p>}
                    <button
                        type="submit"
                        disabled={loading || cooldown > 0}
                        className={`w-full py-3 rounded-lg font-semibold text-white ${loading || cooldown > 0 ? 'bg-gray-600 cursor-not-allowed' : 'bg-red-500 hover:bg-red-600'} transition-colors`}
                    >
                        {loading ? 'Sending...' : cooldown > 0 ? `Wait ${cooldown}s` : 'Get Started Free'}
                    </button>
                    <p className="text-gray-500 text-xs text-center">No password needed. We'll email you a sign-in link.</p>
                </form>
            ) : (
                <div className="text-center space-y-4 bg-gray-800 rounded-xl p-6">
                    <p className="text-white font-medium">Check your email!</p>
                    <p className="text-gray-400 text-sm">
                        We sent a sign-in link to <span className="font-medium text-white">{email}</span>
                    </p>
                    <button
                        onClick={() => { setSent(false); setError(''); }}
                        disabled={cooldown > 0}
                        className={`text-sm ${cooldown > 0 ? 'text-gray-500' : 'text-red-400 hover:text-red-300'}`}
                    >
                        {cooldown > 0 ? `Resend in ${cooldown}s` : 'Send another link'}
                    </button>
                </div>
            )}
        </div>
    );

    const [lightboxImg, setLightboxImg] = useState(null);

    const steps = [
        {
            num: '1',
            title: 'Snap the screen',
            desc: 'Take a photo of the station screen at your workout station. That\'s it.',
            img: '/station3.jpg',
        },
        {
            num: '2',
            title: 'AI extracts everything',
            desc: 'Exercise names, rep counts, and set schemes — pulled from the screen automatically. No typing needed.',
            img: '/screenshot-extracted.png',
        },
        {
            num: '3',
            title: 'Matches your history',
            desc: 'Found this exercise before? It shows your last 5 entries so you know exactly what you lifted last time. Tap "Use" to auto-fill.',
            img: '/screenshot-matches.png',
        },
        {
            num: '4',
            title: 'Track your progress',
            desc: 'See your weight, reps, and difficulty charted over time. Know exactly when to go heavier.',
            img: '/screenshot-analytics.png',
        },
    ];

    return (
        <div className="min-h-screen bg-gray-900 text-white">
            {/* Hero */}
            <div className="px-4 pt-12 pb-12 text-center max-w-2xl mx-auto">
                <div className="flex justify-center mb-6">
                    <img src="/hcf-logo.svg" alt="Hardcore Fitness" className="h-16 sm:h-20" />
                </div>
                <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-2">
                    Hardcore Fitness <span className="text-red-500">Winter Garden</span>
                </h1>
                <p className="text-lg sm:text-xl text-gray-300 mb-1 font-light">
                    AI-Powered Workout Tracker
                </p>
                <p className="text-gray-400 mb-8 max-w-md mx-auto">
                    Snap a photo of the station screen. AI extracts every exercise instantly.
                    Track your weight, reps, and progress over time — no manual entry.
                </p>
                <div className="flex justify-center">
                    {signUpForm}
                </div>
            </div>

            {/* Value props bar */}
            <div className="border-y border-gray-800 py-5 px-4">
                <div className="flex justify-center gap-6 sm:gap-14 text-center max-w-lg mx-auto">
                    <div>
                        <div className="text-xl font-bold text-red-500">AI Capture</div>
                        <div className="text-xs text-gray-500 mt-0.5">Photo in, data out</div>
                    </div>
                    <div>
                        <div className="text-xl font-bold text-white">Auto-Match</div>
                        <div className="text-xs text-gray-500 mt-0.5">Finds your history</div>
                    </div>
                    <div>
                        <div className="text-xl font-bold text-white">Free</div>
                        <div className="text-xs text-gray-500 mt-0.5">No cost, ever</div>
                    </div>
                </div>
            </div>

            {/* Problem section */}
            <div className="px-4 py-16 max-w-2xl mx-auto text-center">
                <h2 className="text-2xl sm:text-3xl font-bold mb-4">
                    You put in the work.<br />
                    <span className="text-gray-400">But are you actually progressing?</span>
                </h2>
                <p className="text-gray-400 max-w-lg mx-auto">
                    Every class, you move through stations, push hard, and leave sweaty.
                    But what did you lift last time? Are you going heavier?
                    Without tracking, you're just guessing.
                </p>
            </div>

            {/* AI chat callout */}
            <div className="px-4 pb-12 max-w-2xl mx-auto">
                <div className="bg-gray-800/60 border border-gray-700/50 rounded-xl p-6 text-center">
                    <h3 className="text-lg font-bold mb-2">
                        Stop losing your workout logs to AI chat limits
                    </h3>
                    <p className="text-gray-400 text-sm max-w-md mx-auto">
                        ChatGPT forgets. RepScan doesn't. Your data is saved permanently,
                        matched automatically, and tracked over time.
                    </p>
                </div>
            </div>

            {/* How it works */}
            <div className="px-4 py-12 max-w-2xl mx-auto">
                <h2 className="text-2xl sm:text-3xl font-bold text-center mb-12">
                    How it works
                </h2>
                <div className="space-y-16">
                    {steps.map((step) => (
                        <div key={step.num} className="flex flex-col items-center text-center">
                            <div className="w-10 h-10 rounded-full bg-red-500 flex items-center justify-center text-white font-bold text-lg mb-4 shrink-0">
                                {step.num}
                            </div>
                            <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                            <p className="text-gray-400 max-w-sm mb-4">{step.desc}</p>
                            {step.img && (
                                <div className={`rounded-xl overflow-hidden shadow-2xl shadow-black/50 border border-gray-700 ${step.num === '1' ? 'max-w-[240px]' : 'max-w-[260px]'}`}>
                                    <img src={step.img} alt={step.title} className="w-full" />
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* App screenshots */}
            <div className="px-4 py-16 max-w-2xl mx-auto text-center">
                <h2 className="text-2xl sm:text-3xl font-bold mb-3">
                    See it in action
                </h2>
                <p className="text-gray-400 mb-8">Dashboard, history, and progress charts — all from your phone. Tap to enlarge.</p>
                <div className="overflow-x-auto pb-2">
                    <div className="inline-flex gap-4 px-2">
                        {[
                            { src: '/screenshot-home.png', label: 'Dashboard' },
                            { src: '/screenshot-history.png', label: 'History' },
                            { src: '/screenshot-analytics.png', label: 'Analytics' },
                            { src: '/screenshot-matches.png', label: 'AI Matching' },
                        ].map((s, i) => (
                            <button key={i} className="shrink-0 text-center group" onClick={() => setLightboxImg(s)}>
                                <div className="rounded-xl overflow-hidden border border-gray-700 shadow-2xl shadow-black/50 w-[150px] sm:w-[200px] group-hover:border-gray-500 transition-colors">
                                    <img src={s.src} alt={s.label} className="w-full" />
                                </div>
                                <p className="text-xs text-gray-500 mt-2 group-hover:text-gray-300 transition-colors">{s.label}</p>
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Features grid */}
            <div className="px-4 py-16 max-w-2xl mx-auto">
                <h2 className="text-2xl sm:text-3xl font-bold text-center mb-10">
                    Built for circuit training
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {[
                        { icon: '📸', title: 'One photo per station', desc: 'Snap the screen, AI extracts all exercises — even multi-exercise stations.' },
                        { icon: '🔍', title: 'Smart matching', desc: 'Automatically finds when you last did the same exercise and shows your history.' },
                        { icon: '📈', title: 'Progress tracking', desc: 'See weight, reps, and difficulty over time. Know exactly when to go heavier.' },
                        { icon: '🔥', title: 'Difficulty rating', desc: 'Rate each exercise Easy to Brutal. See how your perceived effort changes as you get stronger.' },
                    ].map((f) => (
                        <div key={f.title} className="bg-gray-800/50 border border-gray-700/50 rounded-xl p-5">
                            <div className="text-2xl mb-2">{f.icon}</div>
                            <h3 className="font-semibold mb-1">{f.title}</h3>
                            <p className="text-sm text-gray-400">{f.desc}</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* Themes */}
            <div className="px-4 py-16 max-w-2xl mx-auto text-center">
                <h2 className="text-2xl sm:text-3xl font-bold mb-3">
                    Make it yours
                </h2>
                <p className="text-gray-400 mb-8">5 themes to match your style.</p>
                <div className="overflow-x-auto pb-2">
                    <div className="inline-flex gap-4 px-2">
                        {[
                            { src: '/screenshot-home.png', label: 'Dark' },
                            { src: '/theme-light.png', label: 'Light' },
                            { src: '/theme-rose.png', label: 'Rose' },
                            { src: '/theme-midnight.png', label: 'Midnight' },
                            { src: '/theme-neon.png', label: 'Neon' },
                        ].map((t, i) => (
                            <button key={i} className="shrink-0 text-center group" onClick={() => setLightboxImg(t)}>
                                <div className="rounded-xl overflow-hidden border border-gray-700 shadow-2xl shadow-black/50 w-[150px] sm:w-[200px] group-hover:border-gray-500 transition-colors">
                                    <img src={t.src} alt={`${t.label} theme`} className="w-full" />
                                </div>
                                <p className="text-xs text-gray-500 mt-2 group-hover:text-gray-300 transition-colors">{t.label}</p>
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Station photos showcase */}
            <div className="px-4 py-12 max-w-2xl mx-auto text-center">
                <h2 className="text-2xl sm:text-3xl font-bold mb-3">
                    Works with every station
                </h2>
                <p className="text-gray-400 mb-8">Reads any station screen at Hardcore Fitness.</p>
                <div className="flex gap-3 justify-center overflow-x-auto pb-2">
                    {['/station1.jpg', '/station2.jpg', '/station3.jpg'].map((src, i) => (
                        <div key={i} className="rounded-xl overflow-hidden border border-gray-700 shadow-lg shrink-0 w-[140px] sm:w-[180px]">
                            <img src={src} alt={`Station screen ${i + 1}`} className="w-full" />
                        </div>
                    ))}
                </div>
            </div>

            {/* CTA */}
            <div className="px-4 py-16 max-w-2xl mx-auto text-center">
                <h2 className="text-2xl sm:text-3xl font-bold mb-3">
                    Start tracking today
                </h2>
                <p className="text-gray-400 mb-8">Free forever. No app to download. Works on any phone.</p>
                <div className="flex justify-center">
                    {signUpForm}
                </div>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-800 py-6 px-4 text-center space-y-2">
                <a
                    href="https://venmo.com/craigrettew"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-gray-500 hover:text-gray-300 transition-colors inline-block"
                >
                    🥤 Buy me a protein shake
                </a>
                <p className="text-gray-600 text-xs">
                    Powered by <span className="text-gray-500">RepScan</span>. Not affiliated with Hardcore Fitness.
                </p>
            </div>

            {/* Lightbox */}
            {lightboxImg && (
                <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={() => setLightboxImg(null)}>
                    <div className="relative max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
                        <button
                            onClick={() => setLightboxImg(null)}
                            className="absolute -top-10 right-0 text-white/70 hover:text-white text-2xl font-light"
                        >
                            ✕
                        </button>
                        <div className="rounded-2xl overflow-hidden border border-gray-700 shadow-2xl">
                            <img src={lightboxImg.src} alt={lightboxImg.label} className="w-full" />
                        </div>
                        <p className="text-center text-gray-400 text-sm mt-3">{lightboxImg.label}</p>
                    </div>
                </div>
            )}
        </div>
    );
}

// --- Main App Component ---
const PAGE_STORAGE_KEY = 'hcf-current-page';

function AppContent({ onSignOut }) {
    const sessionPayload = decodeToken(getSessionToken());
    const userEmail = sessionPayload?.email || '';

    const authFetch = useCallback(async (url, options = {}) => {
        const token = getSessionToken();
        return fetch(apiUrl(url), {
            ...options,
            headers: {
                ...options.headers,
                'Authorization': `Bearer ${token}`,
            },
        });
    }, []);

    const [page, setPage] = useState(() => {
        const saved = localStorage.getItem(PAGE_STORAGE_KEY);
        return saved || 'home';
    });
    const [workouts, setWorkouts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedExercise, setSelectedExercise] = useState(null);
    const [analyticsOrigin, setAnalyticsOrigin] = useState(null);
    const [error, setError] = useState(null);
    const [historyTargetDate, setHistoryTargetDate] = useState(null);
    const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [isAdminOpen, setIsAdminOpen] = useState(false);
    const [settings, setSettings] = useState(() => {
        const saved = localStorage.getItem(SETTINGS_STORAGE_KEY);
        return saved ? JSON.parse(saved) : defaultSettings;
    });

    // Persist settings to localStorage
    useEffect(() => {
        localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings));
    }, [settings]);

    const pageTitles = {
        home: 'Dashboard',
        capture: 'Log Workout',
        history: 'History',
        analytics: 'Analytics'
    };

    // Persist current page to localStorage
    useEffect(() => {
        localStorage.setItem(PAGE_STORAGE_KEY, page);
    }, [page]);

    // --- Fetch Workouts from API ---
    useEffect(() => {
        const fetchWorkouts = async () => {
            setLoading(true);
            try {
                const response = await authFetch('/api/workouts');
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }
                const data = await response.json();
                const parsedWorkouts = data.map(workout => ({
                    ...workout,
                    id: workout._id,
                    date: new Date(workout.date)
                }));
                setWorkouts(parsedWorkouts);
                setError(null);
            } catch (err) {
                console.error('Failed to fetch workouts:', err);
                setError('Failed to load workouts.');
            } finally {
                setLoading(false);
            }
        };

        fetchWorkouts();
    }, [authFetch]);


    // --- Navigation & Data Management ---
    const handleExerciseSelectAndNavigate = (exerciseName, origin = null) => {
        setSelectedExercise(exerciseName);
        setAnalyticsOrigin(origin);
        setPage('analytics');
    };

    const navigate = (newPage) => {
        setSelectedExercise(null);
        setAnalyticsOrigin(null);
        setPage(newPage);
    };

    const backToCapture = () => {
        setSelectedExercise(null);
        setAnalyticsOrigin(null);
        setPage('capture');
    };

    const navigateToHistoryDate = (dateKey) => {
        setHistoryTargetDate(dateKey);
        setPage('history');
    };

    const deleteExercise = async (workoutId, exerciseIndex) => {
        try {
            const response = await authFetch(`/api/workouts/${workoutId}/exercises/${exerciseIndex}`, { method: 'DELETE' });
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            if (response.status === 204) {
                setWorkouts(prev => prev.filter(workout => workout.id !== workoutId));
            } else {
                const updatedWorkout = await response.json();
                setWorkouts(prev => prev.map(workout => workout.id === workoutId ? { ...updatedWorkout, id: updatedWorkout._id, date: new Date(updatedWorkout.date) } : workout));
            }
            setError(null);
        } catch (err) {
            console.error('Failed to delete exercise:', err);
            setError('Failed to delete exercise.');
        }
    };
    
    const updateExercise = async (workoutId, exerciseIndex, newExerciseData) => {
        const workout = workouts.find(w => w.id === workoutId);
        if (!workout) return;

        const updatedExercises = workout.exercises.map((ex, index) => {
            if (index === exerciseIndex) {
                return { ...ex, ...newExerciseData };
            }
            return ex;
        });

        try {
            const response = await authFetch(`/api/workouts/${workoutId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...workout, exercises: updatedExercises }),
            });
            if (!response.ok) throw new Error('Failed to update exercise');
            const updated = await response.json();
            setWorkouts(prev => prev.map(w => w.id === workoutId ? { ...updated, id: updated._id, date: new Date(updated.date) } : w));
        } catch (err) {
            console.error('Error updating exercise:', err);
            setError('Failed to update exercise. Please try again.');
        }
    };

    const duplicateExercise = async (exerciseData) => {
        const newWorkout = {
            date: new Date(),
            exercises: [{
                name: exerciseData.name.toLowerCase(),
                reps: exerciseData.reps,
                weight: exerciseData.weight,
                seconds: exerciseData.seconds,
                rounds: exerciseData.rounds || 1,
                difficulty: exerciseData.difficulty,
                station: exerciseData.station,
            }],
        };

        try {
            const response = await authFetch('/api/workouts', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(newWorkout),
            });

            if (!response.ok) {
                throw new Error(`Request failed with status ${response.status}`);
            }

            const savedWorkout = await response.json();
            setWorkouts(prev => [{...savedWorkout, id: savedWorkout._id, date: new Date(savedWorkout.date)}, ...prev]);
        } catch (err) {
            console.error('Error duplicating exercise:', err);
            setError('Failed to duplicate exercise. Please try again.');
        }
    };

    const addExerciseToDate = async (dateString, exerciseData) => {
        // Parse the date string (YYYY-MM-DD format)
        const [year, month, day] = dateString.split('-').map(Number);
        const targetDate = new Date(year, month - 1, day, 12, 0, 0); // noon to avoid timezone issues

        // Check if a workout exists for this date
        const existingWorkout = workouts.find(w => {
            const workoutDate = new Date(w.date);
            return workoutDate.toDateString() === targetDate.toDateString();
        });

        if (existingWorkout) {
            // Add exercise to existing workout
            const updatedExercises = [...existingWorkout.exercises, exerciseData];
            try {
                const response = await authFetch(`/api/workouts/${existingWorkout.id}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ ...existingWorkout, exercises: updatedExercises }),
                });
                if (!response.ok) throw new Error('Failed to update workout');
                const updated = await response.json();
                setWorkouts(prev => prev.map(w => w.id === existingWorkout.id ? { ...updated, id: updated._id, date: new Date(updated.date) } : w));
            } catch (err) {
                console.error('Error adding exercise:', err);
                setError('Failed to add exercise. Please try again.');
            }
        } else {
            // Create new workout for this date
            const newWorkout = {
                date: targetDate,
                exercises: [exerciseData],
            };
            try {
                const response = await authFetch('/api/workouts', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(newWorkout),
                });
                if (!response.ok) throw new Error('Failed to create workout');
                const saved = await response.json();
                setWorkouts(prev => [{...saved, id: saved._id, date: new Date(saved.date)}, ...prev]);
            } catch (err) {
                console.error('Error creating workout:', err);
                setError('Failed to add exercise. Please try again.');
            }
        }
    };

    const renderPage = () => {
        if (loading) {
            return <LoadingSpinner settings={settings} />;
        }

        if (error) {
            return (
                <div className={`text-center ${theme.accentText}`}>
                    {error}
                </div>
            );
        }

        switch (page) {
            case 'capture':
                return <CaptureScreen setPage={navigate} workouts={workouts} setWorkouts={setWorkouts} settings={settings} authFetch={authFetch} onViewExerciseHistory={handleExerciseSelectAndNavigate} />;
            case 'history':
                return <HistoryScreen workouts={workouts} onExerciseSelect={handleExerciseSelectAndNavigate} onDeleteExercise={deleteExercise} onUpdateExercise={updateExercise} onDuplicateExercise={duplicateExercise} onAddExercise={addExerciseToDate} targetDate={historyTargetDate} clearTargetDate={() => setHistoryTargetDate(null)} settings={settings} />;
            case 'analytics':
                return <AnalyticsScreen workouts={workouts} selectedExercise={selectedExercise} setSelectedExercise={setSelectedExercise} onNavigateToDate={navigateToHistoryDate} settings={settings} origin={analyticsOrigin} onBackToCapture={backToCapture} />;
            case 'home':
            default:
                return <HomeScreen workouts={workouts} setPage={setPage} settings={settings} />;
        }
    };

    const theme = getTheme(settings?.theme);

    return (
        <div className={`h-screen font-sans overflow-hidden ${theme.outerBg} ${theme.textPrimary}`}>
            <div className={`md:max-w-lg md:mx-auto h-screen shadow-2xl flex flex-col ${theme.pageBg}`}>
                {/* Fixed Header */}
                <header className={`flex-shrink-0 flex items-center justify-between px-4 py-3 z-20 ${theme.pageBg} border-b ${theme.borderLight}`}>
                    <h1 className="text-2xl font-bold">{pageTitles[page]}</h1>
                    <button
                        onClick={() => setIsProfileMenuOpen(true)}
                        className={`p-2 ${theme.hoverBg} rounded-full transition-colors`}
                    >
                        <IconUser className="w-6 h-6" />
                    </button>
                </header>
                <main className="flex-1 pb-24 px-4 overflow-y-auto">
                    {renderPage()}
                </main>
                <BottomNav activePage={page} setPage={navigate} settings={settings} />
            </div>
            <ProfileMenu
                isOpen={isProfileMenuOpen}
                onClose={() => setIsProfileMenuOpen(false)}
                workouts={workouts}
                onOpenSettings={() => {
                    setIsProfileMenuOpen(false);
                    setIsSettingsOpen(true);
                }}
                onOpenAdmin={() => {
                    setIsProfileMenuOpen(false);
                    setIsAdminOpen(true);
                }}
                settings={settings}
                email={userEmail}
                onSignOut={onSignOut}
            />
            <SettingsScreen
                isOpen={isSettingsOpen}
                onClose={() => setIsSettingsOpen(false)}
                settings={settings}
                onUpdateSettings={setSettings}
            />
            <AdminScreen
                isOpen={isAdminOpen}
                onClose={() => setIsAdminOpen(false)}
                settings={settings}
                authFetch={authFetch}
            />
        </div>
    );
}

// --- Helper Functions ---

// Simple string similarity (returns 0-1, higher = more similar)
const stringSimilarity = (input, candidate) => {
    const s1 = normalizeExerciseName(input);
    const s2 = normalizeExerciseName(candidate);
    if (s1 === s2) return 1;
    if (s1.length === 0 || s2.length === 0) return 0;

    const words1 = s1.split(/\s+/);
    const words2 = s2.split(/\s+/);

    // Prefix match: every input word is a prefix of a candidate word (great for typing)
    const allPrefixMatch = words1.every(w1 =>
        words2.some(w2 => w2.startsWith(w1))
    );
    if (allPrefixMatch) {
        // Score higher when more of the candidate is covered
        const coverage = words1.length / words2.length;
        return 0.7 + coverage * 0.25; // 0.7–0.95 depending on coverage
    }

    // Exact word matches — use best coverage from either direction
    const exactMatches = words1.filter(w1 => words2.includes(w1));
    if (exactMatches.length > 0) {
        const coverageOfInput = exactMatches.length / words1.length;
        const coverageOfCandidate = exactMatches.length / words2.length;
        const coverage = Math.max(coverageOfInput, coverageOfCandidate);
        return coverage * 0.85;
    }

    // Partial word matches (substring) — same bidirectional coverage.
    // Require both sides ≥3 chars so short tokens like "DB" don't match
    // any longer word that happens to contain those letters (e.g. "deadbug").
    const partialMatches = words1.filter(w1 =>
        w1.length >= 3 && words2.some(w2 => w2.length >= 3 && (w2.includes(w1) || w1.includes(w2)))
    );
    if (partialMatches.length > 0) {
        const coverageOfInput = partialMatches.length / words1.length;
        const coverageOfCandidate = partialMatches.length / words2.length;
        const coverage = Math.max(coverageOfInput, coverageOfCandidate);
        return coverage * 0.6;
    }

    return 0;
};

// Format exercise name: title case, keep abbreviations uppercase, remove HCFA, fix common issues
const formatExerciseName = (name) => {
    // Remove HCFA, strip trailing periods, and clean up
    let formatted = name
        .replace(/\(?\s*HCFA\s*\)?/gi, '')
        .replace(/\.+$/, '')
        .replace(/\s+/g, ' ')
        .trim();

    // Fix dotted abbreviations: D.B. -> DB, K.B. -> KB, etc.
    formatted = formatted.replace(/\b([A-Za-z])\.([A-Za-z])\.\s?/g, '$1$2 ');

    // Common misspellings
    const corrections = {
        'dumbell': 'dumbbell',
        'dumbel': 'dumbbell',
        'dumbbel': 'dumbbell',
        'kettle bell': 'kettlebell',
        'kettleball': 'kettlebell',
        'tricep': 'triceps',
        'bicep': 'biceps',
        'excercise': 'exercise',
        'exercize': 'exercise',
        'squatt': 'squat',
        'lunges': 'lunge',
        'curls': 'curl',
        'rows': 'row',
        'pres': 'press',
        'extention': 'extension',
        'extentions': 'extension',
    };

    // Common abbreviations to keep uppercase
    const abbreviations = ['DB', 'KB', 'BB', 'EZ', 'TRX', 'GHD', 'RDL', 'SL', 'SA', 'AB'];

    // Convert to title case, preserving abbreviations and fixing misspellings
    formatted = formatted
        .toLowerCase()
        .split(' ')
        .map(word => {
            // Fix misspellings
            if (corrections[word]) {
                word = corrections[word];
            }
            const upper = word.toUpperCase();
            // Keep abbreviations uppercase
            if (abbreviations.includes(upper)) {
                return upper;
            }
            // Capitalize first letter of each word
            return word.charAt(0).toUpperCase() + word.slice(1);
        })
        .join(' ');

    return formatted;
};

// Display exercise name with proper capitalization (KB, DB, etc.)
const displayExerciseName = (name) => {
    if (!name) return '';
    const abbreviations = ['db', 'kb', 'bb', 'ez', 'trx', 'ghd', 'rdl', 'sl', 'sa', 'ab'];
    return name
        .split(' ')
        .map(word => {
            if (abbreviations.includes(word.toLowerCase())) {
                return word.toUpperCase();
            }
            return word.charAt(0).toUpperCase() + word.slice(1);
        })
        .join(' ');
};

// Normalize exercise name for grouping/matching: lowercase, strip trailing periods,
// standardize plural variations so OCR differences don't create duplicates.
const normalizeExerciseName = (name) => {
    if (!name) return '';
    let n = name.toLowerCase().trim().replace(/\.+$/, '');
    // Standardize common plural/singular variations
    const replacements = [
        [/\btricep\b/g, 'triceps'],
        [/\bbicep\b/g, 'biceps'],
        [/\blunges\b/g, 'lunge'],
        [/\bcurls\b/g, 'curl'],
        [/\brows\b/g, 'row'],
        [/\bsquats\b/g, 'squat'],
        [/\bpresses\b/g, 'press'],
        [/\bdeadlifts\b/g, 'deadlift'],
        [/\braises\b/g, 'raise'],
        [/\bcrunches\b/g, 'crunch'],
        [/\bpushdowns\b/g, 'pushdown'],
        [/\bswings\b/g, 'swing'],
        [/\bsnatches\b/g, 'snatch'],
        [/\bflyes\b/g, 'fly'],
        [/\bflies\b/g, 'fly'],
        [/\bextentions?\b/g, 'extension'],
        [/\bexts?\b(?!ension)/g, 'ext'],
    ];
    for (const [pat, rep] of replacements) {
        n = n.replace(pat, rep);
    }
    return n;
};

// Find top N similar exercises with their recent entries
const findTopMatches = (exerciseName, workouts, maxMatches = Infinity, threshold = 0.5) => {
    const normalizedInput = normalizeExerciseName(exerciseName);
    if (normalizedInput.length < 2) return [];

    // Build a map of exercise names to their entries
    const exerciseMap = new Map();
    workouts.forEach(workout => {
        workout.exercises.forEach(ex => {
            const name = normalizeExerciseName(ex.name);
            if (!exerciseMap.has(name)) {
                exerciseMap.set(name, []);
            }
            exerciseMap.get(name).push({
                date: new Date(workout.date),
                weight: ex.weight,
                reps: ex.reps,
                seconds: ex.seconds,
                rounds: ex.rounds,
                difficulty: ex.difficulty,
                station: ex.station,
            });
        });
    });

    // Score each exercise and collect matches
    const matches = [];
    exerciseMap.forEach((entries, name) => {
        const score = stringSimilarity(normalizedInput, name);
        if (score >= threshold) {
            const sortedRaw = entries.sort((a, b) => b.date - a.date);
            const lastDateMs = sortedRaw[0]?.date.getTime() || 0;
            const totalCount = entries.length;
            const sortedEntries = sortedRaw.slice(0, 5).map(e => ({
                ...e,
                date: e.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            }));

            matches.push({
                name,
                score,
                lastDate: sortedEntries[0]?.date || '',
                lastDateMs,
                totalCount,
                entries: sortedEntries,
            });
        }
    });

    // Sort by score (bucketed), then recency, then frequency.
    // Close scores (within 0.05) tie-break on recency so the user's most-recent
    // matching exercise beats an ancient one-off that happens to score higher.
    return matches
        .sort((a, b) => {
            if (Math.abs(a.score - b.score) > 0.05) return b.score - a.score;
            if (a.lastDateMs !== b.lastDateMs) return b.lastDateMs - a.lastDateMs;
            return b.totalCount - a.totalCount;
        })
        .slice(0, maxMatches);
};

// Find the most recent stats for an exercise by name
const findPreviousStats = (exerciseName, workouts) => {
    const normalizedName = normalizeExerciseName(exerciseName);
    let mostRecent = null;
    let mostRecentDate = null;

    workouts.forEach(workout => {
        workout.exercises.forEach(ex => {
            if (normalizeExerciseName(ex.name) === normalizedName) {
                const workoutDate = new Date(workout.date);
                if (!mostRecentDate || workoutDate > mostRecentDate) {
                    mostRecentDate = workoutDate;
                    mostRecent = {
                        weight: ex.weight,
                        reps: ex.reps,
                        seconds: ex.seconds,
                        rounds: ex.rounds,
                        difficulty: ex.difficulty,
                        station: ex.station,
                        date: workoutDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                    };
                }
            }
        });
    });

    return mostRecent;
};


// --- Screens & Components ---

function Modal({ isOpen, onClose, children, cardBg = 'bg-gray-800' }) {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className={`${cardBg} rounded-lg shadow-xl w-full max-w-sm`}>
                {children}
            </div>
        </div>
    );
}

// Match History - collapsible sections showing exercise history per match
const MATCH_HISTORY_INITIAL_LIMIT = 7;
const MatchHistory = ({ matches, onUseEntry, usedEntryKey, settings }) => {
    const [expanded, setExpanded] = useState({});
    const [showAll, setShowAll] = useState(false);
    const theme = getTheme(settings?.theme);

    if (!matches || matches.length === 0) return null;

    const visible = showAll ? matches : matches.slice(0, MATCH_HISTORY_INITIAL_LIMIT);
    const hiddenCount = matches.length - visible.length;

    const formatEntry = (entry) => {
        const rounds = entry.rounds || 1;
        if (entry.seconds) {
            return `${rounds}x ${entry.seconds}s`;
        }
        const reps = entry.reps || 0;
        const weight = entry.weight ? `${entry.weight}lbs` : 'BW';
        return `${rounds}x${reps} @ ${weight}`;
    };

    const entryKey = (match, entry, j) => `${match.name}-${j}`;

    const toggle = (i) => setExpanded(prev => ({ ...prev, [i]: !prev[i] }));

    return (
        <div className="rounded-lg border border-blue-500/40 overflow-hidden mb-2">
            {visible.map((match, i) => {
                const isOpen = !!expanded[i];
                const count = match.entries.length;
                return (
                    <div key={i} className={i > 0 ? `border-t ${theme.border}` : ''}>
                        <button
                            onClick={() => toggle(i)}
                            className={`w-full flex items-center gap-2 px-3 py-2 text-left`}
                        >
                            <span className={`text-base ${theme.textMuted}`}>{isOpen ? '▾' : '▸'}</span>
                            <span className={`text-sm font-medium ${theme.textPrimary}`}>
                                {displayExerciseName(match.name)}
                            </span>
                            <span className={`text-xs ${theme.textMuted}`}>
                                • {count} {count === 1 ? 'match' : 'matches'}
                            </span>
                        </button>
                        {isOpen && (
                            <div className={`${theme.inputBg} px-1`}>
                                {match.entries.map((entry, j) => {
                                    const key = entryKey(match, entry, j);
                                    const isUsed = usedEntryKey === key;
                                    return (
                                    <div key={j} className={`flex items-center px-2 py-1.5 ${j > 0 ? `border-t ${theme.border}` : ''}`}>
                                        <span className={`flex-1 text-xs ${theme.textMuted}`}>
                                            {formatEntry(entry)}
                                            {entry.difficulty ? ` ${'🔥'.repeat(entry.difficulty)}` : ''}
                                            <span className={`ml-2 ${theme.textFaint}`}>{entry.date}</span>
                                        </span>
                                        {isUsed ? (
                                            <span className={`text-xs ${theme.textMuted} px-2`}>✓</span>
                                        ) : (
                                            <button
                                                onClick={() => onUseEntry(match, entry, key)}
                                                className="text-xs text-blue-400 font-semibold px-2 hover:text-blue-300"
                                            >
                                                Use
                                            </button>
                                        )}
                                    </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                );
            })}
            {hiddenCount > 0 && (
                <button
                    onClick={() => setShowAll(true)}
                    className={`w-full px-3 py-2 text-left text-xs border-t ${theme.border} ${theme.textMuted} hover:${theme.textPrimary}`}
                >
                    Show all {matches.length} matches ({hiddenCount} more)
                </button>
            )}
            {showAll && matches.length > MATCH_HISTORY_INITIAL_LIMIT && (
                <button
                    onClick={() => setShowAll(false)}
                    className={`w-full px-3 py-2 text-left text-xs border-t ${theme.border} ${theme.textMuted} hover:${theme.textPrimary}`}
                >
                    Show fewer
                </button>
            )}
        </div>
    );
};

function EditExerciseForm({ exercise, onSave, onCancel, title, settings, showDate }) {
    const theme = getTheme(settings?.theme);
    const [formData, setFormData] = useState({
        name: exercise.name || '',
        reps: exercise.reps || '',
        weight: exercise.weight || '',
        rounds: exercise.rounds || '',
        seconds: exercise.seconds || '',
        difficulty: exercise.difficulty || '',
        station: exercise.station || '',
        date: exercise.date || ''
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSave = (e) => {
        e.preventDefault();
        if (!formData.name.trim()) return;
        const finalData = {
            name: formData.name.trim().toLowerCase(),
            reps: formData.reps ? Number(formData.reps) : null,
            weight: formData.weight ? Number(formData.weight) : null,
            rounds: formData.rounds ? Number(formData.rounds) : 1,
            seconds: formData.seconds ? Number(formData.seconds) : null,
            difficulty: formData.difficulty ? Number(formData.difficulty) : null,
            station: formData.station ? Number(formData.station) : null,
        };
        if (showDate) finalData.date = formData.date;
        onSave(finalData);
    };

    return (
        <form onSubmit={handleSave} className="w-full p-5 space-y-4">
            {title && <p className="text-sm text-green-400 font-semibold">{title}</p>}
            {showDate && (
                <div className="space-y-1">
                    <label className={`text-sm ${theme.textMuted}`}>Date</label>
                    <input type="date" name="date" value={formData.date} onChange={handleChange} className={`w-full ${theme.inputBg} p-2 rounded-md ${theme.textPrimary}`} />
                </div>
            )}
            <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`w-full font-bold text-lg ${theme.inputBg} p-2 rounded-md ${theme.textPrimary} mb-2 capitalize`}
                placeholder="Exercise name"
            />
            <div className="grid grid-cols-2 gap-4">
                {exercise.seconds === null ? (
                    <div className="space-y-1">
                        <label className={`text-sm ${theme.textMuted}`}>Reps</label>
                        <input autoFocus type="number" name="reps" value={formData.reps} onChange={handleChange} className={`w-full ${theme.inputBg} p-2 rounded-md ${theme.textPrimary}`} />
                    </div>
                ) : (
                     <div className="space-y-1">
                        <label className={`text-sm ${theme.textMuted}`}>Seconds</label>
                        <input autoFocus type="number" name="seconds" value={formData.seconds} onChange={handleChange} className={`w-full ${theme.inputBg} p-2 rounded-md ${theme.textPrimary}`} />
                    </div>
                )}
                 <div className="space-y-1">
                    <label className={`text-sm ${theme.textMuted}`}>Weight (lbs)</label>
                    <input type="number" name="weight" placeholder="Bodyweight" value={formData.weight} onChange={handleChange} className={`w-full ${theme.inputBg} p-2 rounded-md ${theme.textPrimary}`} />
                </div>
                 <div className="space-y-1">
                    <label className={`text-sm ${theme.textMuted}`}>Sets</label>
                    <input type="number" name="rounds" value={formData.rounds} onChange={handleChange} className={`w-full ${theme.inputBg} p-2 rounded-md ${theme.textPrimary}`} />
                </div>
                <div className="space-y-1">
                    <label className={`text-sm ${theme.textMuted}`}>Station #</label>
                    <input type="number" name="station" placeholder="—" value={formData.station} onChange={handleChange} className={`w-full ${theme.inputBg} p-2 rounded-md ${theme.textPrimary}`} />
                </div>
                <div className="space-y-1 col-span-2">
                    <label className={`text-sm ${theme.textMuted}`}>Difficulty</label>
                    <select name="difficulty" value={formData.difficulty} onChange={handleChange} className={`w-full ${theme.inputBg} p-2 rounded-md ${theme.textPrimary}`}>
                        <option value="">—</option>
                        <option value="1">🔥 Easy</option>
                        <option value="2">🔥🔥 Moderate</option>
                        <option value="3">🔥🔥🔥 Hard</option>
                        <option value="4">🔥🔥🔥🔥 Brutal</option>
                    </select>
                </div>
            </div>
            <div className="flex gap-3 pt-3">
                 <button type="submit" className="flex-1 bg-green-600 text-white font-bold py-2 rounded-lg hover:bg-green-700 transition-colors">Save</button>
                 <button type="button" onClick={onCancel} className={`flex-1 ${theme.inputBg} text-white font-bold py-2 rounded-lg ${theme.hoverBgLight} transition-colors`}>Cancel</button>
            </div>
        </form>
    );
}


function HistoryScreen({ workouts, onExerciseSelect, onDeleteExercise, onUpdateExercise, onDuplicateExercise, onAddExercise, targetDate, clearTargetDate, settings }) {
    const today = new Date();
    const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    const [viewMonth, setViewMonth] = useState(today.getMonth());
    const [viewYear, setViewYear] = useState(today.getFullYear());
    const [selectedDate, setSelectedDate] = useState(todayKey);
    const [editingExercise, setEditingExercise] = useState(null);
    const [duplicatingExercise, setDuplicatingExercise] = useState(null);
    const [longPressedKey, setLongPressedKey] = useState(null);
    const [expandedGroups, setExpandedGroups] = useState(new Set());
    const [addingExercise, setAddingExercise] = useState(false);
    const [newExercise, setNewExercise] = useState({
        name: '', reps: '', seconds: '', weight: '', rounds: '1', difficulty: '', station: ''
    });
    const [historyMatches, setHistoryMatches] = useState([]);
    const [historyRecentEntries, setHistoryRecentEntries] = useState([]);
    const longPressTimer = useRef(null);

    // Weight formatting
    const weightUnit = settings?.weightUnit || 'lbs';
    const fmtWeight = (w) => w ? (weightUnit === 'kg' ? `${Math.round(w * 0.453592)}kg` : `${w}lbs`) : 'BW';
    const fmtVolume = (v) => weightUnit === 'kg' ? Math.round(v * 0.453592) : v;

    // Theme
    const theme = getTheme(settings?.theme);

    // Handle navigation from Analytics to a specific date
    useEffect(() => {
        if (targetDate) {
            setSelectedDate(targetDate);
            const [year, month] = targetDate.split('-').map(Number);
            setViewYear(year);
            setViewMonth(month - 1);
            clearTargetDate();
        }
    }, [targetDate, clearTargetDate]);

    // Debounced search for add exercise autocomplete
    useEffect(() => {
        if (newExercise.name.length < 2) {
            setHistoryMatches([]);
            return;
        }
        const timer = setTimeout(() => {
            const matches = findTopMatches(newExercise.name, workouts);
            setHistoryMatches(matches);
        }, 200);
        return () => clearTimeout(timer);
    }, [newExercise.name, workouts]);

    const toggleGroup = (groupKey) => {
        setExpandedGroups(prev => {
            const next = new Set(prev);
            if (next.has(groupKey)) {
                next.delete(groupKey);
            } else {
                next.add(groupKey);
            }
            return next;
        });
    };

    // Build a set of dates that have workouts (as YYYY-MM-DD strings)
    const workoutDates = useMemo(() => {
        const dates = new Set();
        workouts.forEach(w => {
            const d = new Date(w.date);
            dates.add(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
        });
        return dates;
    }, [workouts]);

    // Get exercises for selected date, grouped by name
    const groupedExercises = useMemo(() => {
        if (!selectedDate) return [];
        const exercises = [];
        workouts.forEach(w => {
            const d = new Date(w.date);
            const dateKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
            if (dateKey === selectedDate) {
                w.exercises.forEach((ex, index) => {
                    exercises.push({ ...ex, workoutId: w.id, exerciseIndex: index });
                });
            }
        });

        // Group by normalized exercise name
        const groups = {};
        exercises.forEach(ex => {
            const key = normalizeExerciseName(ex.name);
            if (!groups[key]) {
                groups[key] = {
                    name: ex.name,
                    entries: [],
                    totalReps: 0,
                    totalRounds: 0,
                    totalSeconds: 0,
                    maxWeight: 0,
                    maxDifficulty: 0,
                    stations: new Set()
                };
            }
            groups[key].entries.push(ex);
            groups[key].totalReps += (ex.reps || 0) * (ex.rounds || 1);
            groups[key].totalRounds += ex.rounds || 1;
            groups[key].totalSeconds += (ex.seconds || 0) * (ex.rounds || 1);
            groups[key].maxWeight = Math.max(groups[key].maxWeight, ex.weight || 0);
            groups[key].maxDifficulty = Math.max(groups[key].maxDifficulty, ex.difficulty || 0);
            if (ex.station) groups[key].stations.add(ex.station);
        });

        return Object.values(groups).sort((a, b) => a.name.localeCompare(b.name));
    }, [selectedDate, workouts]);

    // Flat list for stats calculation
    const selectedExercises = useMemo(() => {
        return groupedExercises.flatMap(g => g.entries);
    }, [groupedExercises]);

    // Compute stats for selected date
    const selectedDateStats = useMemo(() => {
        if (selectedExercises.length === 0) return null;
        let totalVolume = 0;
        let heaviest = { name: '', weight: 0 };
        let topVolume = { name: '', volume: 0 };
        const uniqueNames = new Set();

        selectedExercises.forEach(ex => {
            uniqueNames.add(normalizeExerciseName(ex.name));
            const volume = (ex.reps || 0) * (ex.rounds || 1) * (ex.weight || 0);
            totalVolume += volume;
            if ((ex.weight || 0) > heaviest.weight) {
                heaviest = { name: ex.name, weight: ex.weight };
            }
            if (volume > topVolume.volume) {
                topVolume = { name: ex.name, volume };
            }
        });

        return {
            totalVolume,
            uniqueExercises: uniqueNames.size,
            heaviest,
            topVolume
        };
    }, [selectedExercises]);

    // Calendar helpers
    const getDaysInMonth = (month, year) => new Date(year, month + 1, 0).getDate();
    const getFirstDayOfMonth = (month, year) => new Date(year, month, 1).getDay();

    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    const goToPrevMonth = () => {
        if (viewMonth === 0) {
            setViewMonth(11);
            setViewYear(viewYear - 1);
        } else {
            setViewMonth(viewMonth - 1);
        }
    };

    const goToNextMonth = () => {
        if (viewMonth === 11) {
            setViewMonth(0);
            setViewYear(viewYear + 1);
        } else {
            setViewMonth(viewMonth + 1);
        }
    };

    // Build calendar grid
    const daysInMonth = getDaysInMonth(viewMonth, viewYear);
    const firstDay = getFirstDayOfMonth(viewMonth, viewYear);
    const calendarDays = [];

    // Empty cells for days before the first day
    for (let i = 0; i < firstDay; i++) {
        calendarDays.push(null);
    }
    // Actual days
    for (let day = 1; day <= daysInMonth; day++) {
        calendarDays.push(day);
    }

    const handleTouchStart = (exerciseKey) => {
        longPressTimer.current = setTimeout(() => {
            setLongPressedKey(exerciseKey);
        }, 500);
    };

    const handleTouchEnd = () => {
        if (longPressTimer.current) {
            clearTimeout(longPressTimer.current);
            longPressTimer.current = null;
        }
    };

    const handleTouchMove = () => {
        if (longPressTimer.current) {
            clearTimeout(longPressTimer.current);
            longPressTimer.current = null;
        }
    };

    const clearLongPress = () => setLongPressedKey(null);

    const handleSaveEdit = async (newExerciseData) => {
        if (editingExercise) {
            const { date, ...exerciseFields } = newExerciseData;
            if (date && date !== editingExercise.date) {
                // Date changed — move exercise to new date
                await onDeleteExercise(editingExercise.workoutId, editingExercise.exerciseIndex);
                await onAddExercise(date, exerciseFields);
                setSelectedDate(date);
                const [y, m] = date.split('-').map(Number);
                setViewYear(y);
                setViewMonth(m - 1);
            } else {
                onUpdateExercise(editingExercise.workoutId, editingExercise.exerciseIndex, exerciseFields);
            }
            setEditingExercise(null);
        }
    };

    const handleAddExercise = async () => {
        if (!newExercise.name || (!newExercise.reps && !newExercise.seconds)) return;

        const exerciseData = {
            name: newExercise.name.toLowerCase(),
            reps: newExercise.reps ? Number(newExercise.reps) : null,
            seconds: newExercise.seconds ? Number(newExercise.seconds) : null,
            weight: newExercise.weight ? Number(newExercise.weight) : null,
            rounds: newExercise.rounds ? Number(newExercise.rounds) : 1,
            difficulty: newExercise.difficulty ? Number(newExercise.difficulty) : null,
            station: newExercise.station ? Number(newExercise.station) : null,
        };

        await onAddExercise(selectedDate, exerciseData);
        setNewExercise({ name: '', reps: '', seconds: '', weight: '', rounds: '1', difficulty: '', station: '' });
        setAddingExercise(false);
    };

    const handleSaveDuplicate = async (newExerciseData) => {
        if (duplicatingExercise) {
            await onDuplicateExercise({
                name: duplicatingExercise.name,
                ...newExerciseData
            });
            setDuplicatingExercise(null);
        }
    };


    const handleHistoryPickExercise = (match) => {
        setNewExercise({ ...newExercise, name: displayExerciseName(match.name) });
        setHistoryRecentEntries(match.entries);
        setHistoryMatches([]);
    };

    const handleHistoryPickEntry = (entry) => {
        setNewExercise({
            ...newExercise,
            weight: entry.weight || '',
            reps: entry.reps || '',
            seconds: entry.seconds || '',
            rounds: entry.rounds || '1',
            difficulty: entry.difficulty || '',
        });
        setHistoryRecentEntries([]);
    };


    const handleDayClick = (day) => {
        if (!day) return;
        const dateKey = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        setSelectedDate(selectedDate === dateKey ? null : dateKey);
        clearLongPress();
    };

    const formatSelectedDate = () => {
        if (!selectedDate) return '';
        const [y, m, d] = selectedDate.split('-');
        const date = new Date(Number(y), Number(m) - 1, Number(d));
        return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
    };

    return (
        <>
            <Modal isOpen={!!editingExercise} onClose={() => setEditingExercise(null)} cardBg={theme.cardBg}>
                {editingExercise && (
                    <EditExerciseForm
                        exercise={editingExercise}
                        onSave={handleSaveEdit}
                        onCancel={() => setEditingExercise(null)}
                        settings={settings}
                        showDate
                    />
                )}
            </Modal>
            <Modal isOpen={!!duplicatingExercise} onClose={() => setDuplicatingExercise(null)} cardBg={theme.cardBg}>
                {duplicatingExercise && (
                    <EditExerciseForm
                        exercise={duplicatingExercise}
                        onSave={handleSaveDuplicate}
                        onCancel={() => setDuplicatingExercise(null)}
                        title="Duplicate Exercise"
                        settings={settings}
                    />
                )}
            </Modal>
            <div>
                {/* Calendar Header */}
                <div className="flex items-center justify-between mb-4">
                    <button onClick={goToPrevMonth} className={`p-2 rounded-full ${theme.hoverBg}`}>
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <div className="flex items-center gap-2">
                        <select
                            value={viewMonth}
                            onChange={(e) => setViewMonth(Number(e.target.value))}
                            className={`p-2 rounded-md ${theme.cardBg} ${theme.textPrimary}`}
                        >
                            {monthNames.map((name, i) => (
                                <option key={name} value={i}>{name}</option>
                            ))}
                        </select>
                        <select
                            value={viewYear}
                            onChange={(e) => setViewYear(Number(e.target.value))}
                            className={`p-2 rounded-md ${theme.cardBg} ${theme.textPrimary}`}
                        >
                            {Array.from({ length: 10 }, (_, i) => today.getFullYear() - 5 + i).map(year => (
                                <option key={year} value={year}>{year}</option>
                            ))}
                        </select>
                    </div>
                    <button onClick={goToNextMonth} className={`p-2 rounded-full ${theme.hoverBg}`}>
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                    </button>
                </div>

                {/* Calendar Grid */}
                <div className={`rounded-lg p-3 mb-4 ${theme.cardBg}`}>
                    {/* Day headers */}
                    <div className="grid grid-cols-7 gap-1 mb-2">
                        {dayNames.map(day => (
                            <div key={day} className={`text-center text-xs ${theme.textMuted} font-semibold py-1`}>
                                {day}
                            </div>
                        ))}
                    </div>
                    {/* Calendar days */}
                    <div className="grid grid-cols-7 gap-1">
                        {calendarDays.map((day, index) => {
                            if (!day) {
                                return <div key={`empty-${index}`} className="aspect-square" />;
                            }
                            const dateKey = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                            const hasWorkout = workoutDates.has(dateKey);
                            const isSelected = selectedDate === dateKey;
                            const isToday = day === today.getDate() && viewMonth === today.getMonth() && viewYear === today.getFullYear();

                            return (
                                <button
                                    key={day}
                                    onClick={() => handleDayClick(day)}
                                    className={`aspect-square rounded-full flex flex-col items-center justify-center relative transition-colors
                                        ${isSelected ? `${theme.selectedDayBg} text-white` : isToday ? theme.inputBg : theme.hoverBg}
                                    `}
                                >
                                    <span className="text-sm">{day}</span>
                                    {hasWorkout && (
                                        <span className={`absolute bottom-1 w-1.5 h-1.5 rounded-full ${isSelected ? 'bg-white' : theme.dotColor}`} />
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Selected Date Exercises */}
                {selectedDate && (
                    <div onClick={() => longPressedKey && clearLongPress()}>
                        <h2 className={`text-lg font-semibold ${theme.textMuted} mb-2`}>{formatSelectedDate()}</h2>
                        {selectedDateStats && (
                            <div className={`text-sm ${theme.textMuted} mb-3 space-y-1`}>
                                <p>{selectedDateStats.uniqueExercises} exercises • {fmtVolume(selectedDateStats.totalVolume).toLocaleString()} {weightUnit} volume</p>
                                {selectedDateStats.heaviest.weight > 0 && (
                                    <p>Heaviest: {fmtWeight(selectedDateStats.heaviest.weight)} ({displayExerciseName(selectedDateStats.heaviest.name)})</p>
                                )}
                            </div>
                        )}
                        <button
                            onClick={() => setAddingExercise(true)}
                            className={`w-full text-center ${theme.accentText} font-semibold py-2 mb-3`}
                        >
                            + Add Exercise
                        </button>
                        {groupedExercises.length === 0 ? (
                            <div className="text-center py-4">
                                <p className={`${theme.textFaint}`}>No exercises logged this day</p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {groupedExercises.map((group) => {
                                    const groupKey = normalizeExerciseName(group.name);
                                    const isExpanded = expandedGroups.has(groupKey);
                                    const hasMultiple = group.entries.length > 1;

                                    const showGroupActions = longPressedKey === groupKey;
                                    const firstEntry = group.entries[0];

                                    // Format combined display
                                    let combinedDisplay = '';
                                    if (group.totalSeconds > 0) {
                                        combinedDisplay = `${group.totalRounds}x ${group.totalSeconds}s`;
                                    } else {
                                        combinedDisplay = `${group.totalReps} ${group.totalReps === 1 ? 'rep' : 'reps'}`;
                                        if (group.totalRounds > 1) combinedDisplay += ` (${group.totalRounds} ${group.totalRounds === 1 ? 'set' : 'sets'})`;
                                    }
                                    if (group.maxWeight > 0) combinedDisplay += hasMultiple ? ` @ ${fmtWeight(group.maxWeight)} max` : ` @ ${fmtWeight(group.maxWeight)}`;
                                    if (group.stations.size > 0) combinedDisplay += ` • St.${[...group.stations].join(',')}`;
                                    if (group.maxDifficulty > 0) combinedDisplay += ` • ${'🔥'.repeat(group.maxDifficulty)}`;

                                    // Format individual entry display
                                    const formatEntry = (ex) => {
                                        let d = '';
                                        if (ex.seconds) {
                                            d = `${ex.rounds || 1}x ${ex.seconds}s`;
                                        } else {
                                            d = `${ex.reps || 0} ${(ex.reps || 0) === 1 ? 'rep' : 'reps'} x ${ex.rounds || 1}`;
                                        }
                                        if (ex.weight) d += ` @ ${fmtWeight(ex.weight)}`;
                                        if (ex.station) d += ` • St.${ex.station}`;
                                        if (ex.difficulty) d += ` • ${'🔥'.repeat(ex.difficulty)}`;
                                        return d;
                                    };

                                    return (
                                        <div key={groupKey}>
                                            {/* Combined header row */}
                                            <div
                                                className={`p-4 rounded-lg shadow-md flex items-center gap-3 ${theme.cardBg} ${showGroupActions ? `ring-2 ${theme.ringColor}` : ''}`}
                                                onClick={() => {
                                                    if (longPressedKey) {
                                                        clearLongPress();
                                                        return;
                                                    }
                                                    if (hasMultiple) {
                                                        toggleGroup(groupKey);
                                                    } else {
                                                        onExerciseSelect(group.name);
                                                    }
                                                }}
                                                onTouchStart={() => handleTouchStart(groupKey)}
                                                onTouchEnd={handleTouchEnd}
                                                onTouchMove={handleTouchMove}
                                                onContextMenu={(e) => { e.preventDefault(); setLongPressedKey(groupKey); }}
                                            >
                                                {hasMultiple && (
                                                    <svg className={`w-5 h-5 ${theme.textMuted} transition-transform ${isExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                    </svg>
                                                )}
                                                <div className="flex-grow cursor-pointer">
                                                    <p className="font-bold text-lg">
                                                        {displayExerciseName(group.name)}
                                                        {hasMultiple && <span className={`${theme.textMuted} text-sm ml-2`}>({group.entries.length})</span>}
                                                    </p>
                                                    <p className={`${theme.accentText} text-md`}>{combinedDisplay}</p>
                                                </div>
                                                {showGroupActions && (
                                                    <div className="flex items-center gap-2">
                                                        <button onClick={(e) => { e.stopPropagation(); setDuplicatingExercise({...firstEntry}); clearLongPress(); }} className="p-3 bg-green-800/50 rounded-full hover:bg-green-500 transition-colors">
                                                            <IconCopy className="w-5 h-5 text-green-400" />
                                                        </button>
                                                        <button onClick={(e) => { e.stopPropagation(); setEditingExercise({...firstEntry, date: selectedDate}); clearLongPress(); }} className="p-3 bg-blue-800/50 rounded-full hover:bg-blue-500 transition-colors">
                                                            <IconEdit className="w-5 h-5 text-blue-400" />
                                                        </button>
                                                        <button onClick={(e) => {
                                                            e.stopPropagation();
                                                            group.entries.forEach(entry => onDeleteExercise(entry.workoutId, entry.exerciseIndex));
                                                            clearLongPress();
                                                        }} className="p-3 bg-red-800/50 rounded-full hover:bg-red-500 transition-colors">
                                                            <IconTrash2 className="w-5 h-5 text-red-400" />
                                                        </button>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Expanded individual entries */}
                                            {isExpanded && (
                                                <div className="ml-6 mt-2 space-y-2">
                                                    {group.entries.map((ex) => {
                                                        const exerciseKey = `${ex.workoutId}-${ex.exerciseIndex}`;
                                                        const showActions = longPressedKey === exerciseKey;
                                                        return (
                                                            <div
                                                                key={exerciseKey}
                                                                className={`${theme.inputBg} p-3 rounded-lg flex items-center gap-3 ${showActions ? `ring-2 ${theme.ringColor}` : ''}`}
                                                                onTouchStart={() => handleTouchStart(exerciseKey)}
                                                                onTouchEnd={handleTouchEnd}
                                                                onTouchMove={handleTouchMove}
                                                                onContextMenu={(e) => { e.preventDefault(); setLongPressedKey(exerciseKey); }}
                                                            >
                                                                <div
                                                                    onClick={(e) => {
                                                                        if (longPressedKey) {
                                                                            e.stopPropagation();
                                                                            clearLongPress();
                                                                            return;
                                                                        }
                                                                        onExerciseSelect(ex.name);
                                                                    }}
                                                                    className="flex-grow cursor-pointer"
                                                                >
                                                                    <p className={`${theme.accentText} text-sm`}>{formatEntry(ex)}</p>
                                                                </div>
                                                                {showActions && (
                                                                    <div className="flex items-center gap-2">
                                                                        <button onClick={(e) => { e.stopPropagation(); setDuplicatingExercise({...ex}); clearLongPress(); }} className="p-2 bg-green-800/50 rounded-full hover:bg-green-500 transition-colors">
                                                                            <IconCopy className="w-4 h-4 text-green-400" />
                                                                        </button>
                                                                        <button onClick={(e) => { e.stopPropagation(); setEditingExercise({...ex, date: selectedDate}); clearLongPress(); }} className="p-2 bg-blue-800/50 rounded-full hover:bg-blue-500 transition-colors">
                                                                            <IconEdit className="w-4 h-4 text-blue-400" />
                                                                        </button>
                                                                        <button onClick={(e) => { e.stopPropagation(); onDeleteExercise(ex.workoutId, ex.exerciseIndex); clearLongPress(); }} className="p-2 bg-red-800/50 rounded-full hover:bg-red-500 transition-colors">
                                                                            <IconTrash2 className="w-4 h-4 text-red-400" />
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}
            </div>
            <Modal isOpen={addingExercise} onClose={() => setAddingExercise(false)} cardBg={theme.cardBg}>
                <div className="p-5 space-y-4">
                    <p className={`font-bold text-lg ${theme.textPrimary} mb-2`}>Add Exercise to {formatSelectedDate()}</p>
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Exercise Name"
                            value={newExercise.name}
                            onChange={e => { setNewExercise({...newExercise, name: e.target.value}); setHistoryRecentEntries([]); }}
                            className={`w-full ${theme.inputBg} p-2 rounded-md ${theme.textPrimary}`}
                            autoFocus
                        />
                        {historyMatches.length > 0 && historyRecentEntries.length === 0 && (
                            <div className={`absolute z-10 left-0 right-0 mt-1 ${theme.cardBg} rounded-lg border ${theme.border} shadow-lg max-h-48 overflow-y-auto`}>
                                {historyMatches.map((match, i) => (
                                    <button
                                        key={i}
                                        className={`w-full text-left px-3 py-2 ${i > 0 ? `border-t ${theme.border}` : ''}`}
                                        onClick={() => handleHistoryPickExercise(match)}
                                    >
                                        <span className={`block text-sm font-medium ${theme.textPrimary}`}>{displayExerciseName(match.name)}</span>
                                        <span className={`block text-xs ${theme.textMuted}`}>{match.entries.length} log{match.entries.length === 1 ? '' : 's'} • last {match.lastDate}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    {historyRecentEntries.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                            {historyRecentEntries.map((entry, i) => {
                                const label = entry.seconds
                                    ? `${entry.rounds || 1}x ${entry.seconds}s`
                                    : `${entry.reps || 0}${entry.weight ? ` @ ${entry.weight}lbs` : ' BW'}`;
                                return (
                                    <button
                                        key={i}
                                        onClick={() => handleHistoryPickEntry(entry)}
                                        className="text-xs bg-blue-600/30 text-blue-300 border border-blue-500/50 px-3 py-1.5 rounded-full hover:bg-blue-600/50 transition-colors"
                                    >
                                        {label} • {entry.date}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                    <div className="grid grid-cols-2 gap-2">
                        <input
                            type="number"
                            placeholder="Reps"
                            value={newExercise.reps}
                            onChange={e => setNewExercise({...newExercise, reps: e.target.value, seconds: ''})}
                            className={`w-full ${theme.inputBg} p-2 rounded-md ${theme.textPrimary}`}
                        />
                        <input
                            type="number"
                            placeholder="OR Seconds"
                            value={newExercise.seconds}
                            onChange={e => setNewExercise({...newExercise, seconds: e.target.value, reps: ''})}
                            className={`w-full ${theme.inputBg} p-2 rounded-md ${theme.textPrimary}`}
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <input
                            type="number"
                            placeholder="Sets"
                            value={newExercise.rounds}
                            onChange={e => setNewExercise({...newExercise, rounds: e.target.value})}
                            className={`w-full ${theme.inputBg} p-2 rounded-md ${theme.textPrimary}`}
                        />
                        <input
                            type="number"
                            placeholder="Weight (optional)"
                            value={newExercise.weight}
                            onChange={e => setNewExercise({...newExercise, weight: e.target.value})}
                            className={`w-full ${theme.inputBg} p-2 rounded-md ${theme.textPrimary}`}
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <input
                            type="number"
                            placeholder="Station #"
                            value={newExercise.station}
                            onChange={e => setNewExercise({...newExercise, station: e.target.value})}
                            className={`w-full ${theme.inputBg} p-2 rounded-md ${theme.textPrimary}`}
                        />
                        <select
                            value={newExercise.difficulty}
                            onChange={e => setNewExercise({...newExercise, difficulty: e.target.value})}
                            className={`w-full ${theme.inputBg} p-2 rounded-md ${theme.textPrimary}`}
                        >
                            <option value="">Difficulty</option>
                            <option value="1">🔥 Easy</option>
                            <option value="2">🔥🔥 Moderate</option>
                            <option value="3">🔥🔥🔥 Hard</option>
                            <option value="4">🔥🔥🔥🔥 Brutal</option>
                        </select>
                    </div>
                    <div className="flex gap-3 pt-3">
                        <button
                            onClick={handleAddExercise}
                            className="flex-1 bg-green-600 text-white font-bold py-2 rounded-lg hover:bg-green-700 transition-colors"
                        >
                            Add
                        </button>
                        <button
                            onClick={() => setAddingExercise(false)}
                            className="flex-1 bg-gray-600 text-white font-bold py-2 rounded-lg hover:bg-gray-500 transition-colors"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </Modal>
        </>
    );
}

function HomeScreen({ workouts, setPage, settings }) {
    const summary = useMemo(() => {
        if (!workouts || workouts.length === 0) return null;

        // Find the most recent date
        const sortedWorkouts = [...workouts].sort((a, b) => new Date(b.date) - new Date(a.date));
        const mostRecentDate = new Date(sortedWorkouts[0].date);
        const mostRecentDateStr = mostRecentDate.toDateString();

        // Get all workouts from that day
        const todaysWorkouts = sortedWorkouts.filter(w =>
            new Date(w.date).toDateString() === mostRecentDateStr
        );

        let totalVolume = 0;
        let totalExercises = 0;
        let highestWeightExercise = { name: 'N/A', weight: 0 };
        let highestVolumeExercise = { name: 'N/A', volume: 0 };
        const uniqueExerciseNames = new Set();

        todaysWorkouts.forEach(workout => {
            totalExercises += workout.exercises.length;
            workout.exercises.forEach(ex => {
                uniqueExerciseNames.add(normalizeExerciseName(ex.name));
                const exerciseVolume = (ex.reps || 0) * (ex.rounds || 1) * (ex.weight || 0);
                totalVolume += exerciseVolume;

                if ((ex.weight || 0) > highestWeightExercise.weight) {
                    highestWeightExercise = { name: ex.name, weight: ex.weight };
                }

                if (exerciseVolume > highestVolumeExercise.volume) {
                    highestVolumeExercise = { name: ex.name, volume: exerciseVolume };
                }
            });
        });

        return {
            date: mostRecentDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }),
            totalExercises,
            uniqueExercises: uniqueExerciseNames.size,
            totalVolume,
            highestWeightExercise,
            highestVolumeExercise,
        };
    }, [workouts]);

    const theme = getTheme(settings?.theme);

    if (!summary) {
        return (
            <div className="text-center mt-20">
                <h2 className="text-2xl font-bold mb-2">No Workouts Yet</h2>
                <p className={`${theme.textMuted}`}>Tap the 'Capture' button to log your first workout!</p>
            </div>
        );
    }

    const MetricCard = ({ title, value, unit, subtext, icon }) => (
        <div className={`p-4 rounded-lg flex items-center gap-4 ${theme.cardBg}`}>
            <div className={`${theme.accentBgSoft} p-3 rounded-full`}>
                {icon}
            </div>
            <div>
                <p className={`text-sm ${theme.textMuted}`}>{title}</p>
                <p className="text-xl font-bold">{value} <span className="text-base font-normal">{unit}</span></p>
                {subtext && <p className={`text-xs ${theme.textFaint}`}>{subtext}</p>}
            </div>
        </div>
    );

    const weightUnit = settings?.weightUnit || 'lbs';
    const convertWeight = (lbs) => weightUnit === 'kg' ? Math.round(lbs * 0.453592) : lbs;

    return (
        <div>
            <p className={`${theme.textMuted} mb-6`}>Summary of your last workout on {summary.date}.</p>

            <div className="space-y-3">
                <MetricCard
                    title="Total Volume"
                    value={convertWeight(summary.totalVolume).toLocaleString()}
                    unit={weightUnit}
                    icon={<IconWeight className={theme.accentText} />}
                />
                 <MetricCard
                    title="Unique Exercises"
                    value={summary.uniqueExercises}
                    icon={<IconDumbbell className={theme.accentText} />}
                />
                 <MetricCard
                    title="Heaviest Lift"
                    value={convertWeight(summary.highestWeightExercise.weight)}
                    unit={weightUnit}
                    subtext={displayExerciseName(summary.highestWeightExercise.name)}
                    icon={<IconBarChart2 className={theme.accentText} />}
                />
                 <MetricCard
                    title="Top Volume Exercise"
                    value={convertWeight(summary.highestVolumeExercise.volume).toLocaleString()}
                    unit={weightUnit}
                    subtext={displayExerciseName(summary.highestVolumeExercise.name)}
                    icon={<IconRepeat className={theme.accentText} />}
                />
            </div>

            <button
                onClick={() => setPage('history')}
                className={`mt-8 w-full ${theme.inputBg} ${theme.hoverBgLight} text-white font-bold py-3 px-4 rounded-lg flex items-center justify-center transition-colors`}
            >
                View Full Workout History
            </button>
        </div>
    );
}

const CAPTURE_STORAGE_KEY = 'hcf-capture-state';

function CaptureScreen({ setPage, workouts, setWorkouts, settings, authFetch, onViewExerciseHistory }) {
    const savedCaptureState = useMemo(() => {
        const saved = localStorage.getItem(CAPTURE_STORAGE_KEY);
        if (!saved) return {};
        try { return JSON.parse(saved) || {}; } catch { return {}; }
    }, []);
    const [image, setImage] = useState(null);
    const [parsedExercises, setParsedExercises] = useState(savedCaptureState.exercises || []);
    const [isParsing, setIsParsing] = useState(false);
    const [error, setError] = useState('');
    const [manualEntry, setManualEntry] = useState(savedCaptureState.manualEntry || { name: '', reps: '', weight: '', seconds: '', sets: '', difficulty: '', station: '' });
    const [manualMatches, setManualMatches] = useState([]);
    const [manualRecentEntries, setManualRecentEntries] = useState(savedCaptureState.manualRecentEntries || []);
    const [suppressManualMatches, setSuppressManualMatches] = useState(savedCaptureState.suppressManualMatches || false);
    const [showAllManualMatches, setShowAllManualMatches] = useState(false);
    const [showManualForm, setShowManualForm] = useState(savedCaptureState.showManualForm || false);
    const [successMessage, setSuccessMessage] = useState('');
    const [successFading, setSuccessFading] = useState(false);
    const [usedEntryKeys, setUsedEntryKeys] = useState({});
    const fileInputRef = useRef(null);
    const galleryInputRef = useRef(null);

    const theme = getTheme(settings?.theme);

    // Persist capture state to localStorage
    useEffect(() => {
        if (image || parsedExercises.length > 0 || showManualForm) {
            localStorage.setItem(CAPTURE_STORAGE_KEY, JSON.stringify({
                exercises: parsedExercises,
                manualEntry,
                showManualForm,
                manualRecentEntries,
                suppressManualMatches,
            }));
        }
    }, [image, parsedExercises, manualEntry, showManualForm, manualRecentEntries, suppressManualMatches]);

    // Debounced search for manual entry autocomplete
    useEffect(() => {
        if (suppressManualMatches || manualEntry.name.length < 2) {
            setManualMatches([]);
            return;
        }
        const timer = setTimeout(() => {
            const matches = findTopMatches(manualEntry.name, workouts);
            setManualMatches(matches);
        }, 200);
        return () => clearTimeout(timer);
    }, [manualEntry.name, workouts, suppressManualMatches]);

    const compressImage = (file, maxDimension = 1280, quality = 0.75) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                const img = new Image();
                img.onload = () => {
                    let { width, height } = img;
                    if (width > height && width > maxDimension) {
                        height = Math.round((height * maxDimension) / width);
                        width = maxDimension;
                    } else if (height >= width && height > maxDimension) {
                        width = Math.round((width * maxDimension) / height);
                        height = maxDimension;
                    }
                    const canvas = document.createElement('canvas');
                    canvas.width = width;
                    canvas.height = height;
                    canvas.getContext('2d').drawImage(img, 0, 0, width, height);
                    resolve(canvas.toDataURL('image/jpeg', quality));
                };
                img.onerror = reject;
                img.src = e.target.result;
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    };

    const handleImageChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setImage(URL.createObjectURL(file));
        try {
            const dataUrl = await compressImage(file);
            parseImageWithAI(dataUrl.split(',')[1]);
        } catch (err) {
            console.error('[HCF] Image compression failed', err);
            setError('Could not process the image. Try another photo.');
        }
    };

    const callGeminiForExercises = async (base64ImageData) => {
        const response = await authFetch('/api/extract-exercises', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ image: base64ImageData })
        });

        if (!response.ok) {
            let detail = '';
            try { detail = JSON.stringify(await response.json()); } catch {}
            console.error('[HCF extract] API error', response.status, detail);
            throw new Error(`Extract request failed with status ${response.status}`);
        }

        const { exercises } = await response.json();
        if (!Array.isArray(exercises) || exercises.length === 0) {
            throw new Error('No exercises returned');
        }
        return exercises;
    };

    const parseImageWithAI = async (base64ImageData) => {
        setIsParsing(true);
        setError('');
        setParsedExercises([]);

        let parsedJson;
        let lastError;
        for (let attempt = 1; attempt <= 2; attempt++) {
            try {
                parsedJson = await callGeminiForExercises(base64ImageData);
                break;
            } catch (err) {
                lastError = err;
                console.warn(`[HCF Gemini] Attempt ${attempt} failed:`, err.message);
            }
        }

        if (!parsedJson) {
            console.error("[HCF Gemini] All attempts failed:", lastError);
            setError("Couldn't read the exercises. Please try another photo or add them manually.");
            setIsParsing(false);
            return;
        }

        try {
            const exercisesWithSuggestions = parsedJson.map(ex => {
                const formattedName = formatExerciseName(ex.name);
                const topMatches = findTopMatches(formattedName, workouts);
                const prevStats = findPreviousStats(formattedName, workouts);
                const reps = (ex.reps === -1 || ex.reps == null) ? '' : ex.reps;
                const seconds = (ex.seconds === -1 || ex.seconds == null) ? '' : ex.seconds;
                return {
                    ...ex,
                    name: formattedName,
                    reps,
                    seconds,
                    weight: prevStats?.weight || '',
                    rounds: prevStats?.rounds || 1,
                    difficulty: prevStats?.difficulty || '',
                    station: ex.station || prevStats?.station || '',
                    id: Math.random(),
                    topMatches,
                    prevStats
                };
            });
            setParsedExercises(exercisesWithSuggestions);
        } catch (err) {
            console.error("[HCF Gemini] Post-processing error:", err);
            setError("Couldn't read the exercises. Please try another photo or add them manually.");
        } finally {
            setIsParsing(false);
        }
    };
    
    const handleLogUpdate = (index, field, value) => {
        const updatedExercises = [...parsedExercises];
        updatedExercises[index][field] = value;
        setParsedExercises(updatedExercises);
    };
    
    const handleDeleteParsedExercise = (id) => {
        setParsedExercises(parsedExercises.map(ex =>
            ex.id === id ? { ...ex, isDeleted: true } : ex
        ));
    };

    const handleRestoreExercise = (id) => {
        setParsedExercises(parsedExercises.map(ex =>
            ex.id === id ? { ...ex, isDeleted: false } : ex
        ));
    };

    const handleRestoreAll = () => {
        setParsedExercises(parsedExercises.map(ex => ({ ...ex, isDeleted: false })));
    };

    const handleDuplicateExercise = (exerciseId) => {
        const exerciseToDuplicate = parsedExercises.find(ex => ex.id === exerciseId);
        if (exerciseToDuplicate) {
            const duplicated = {
                ...exerciseToDuplicate,
                id: Math.random()
            };
            const index = parsedExercises.findIndex(ex => ex.id === exerciseId);
            const newExercises = [...parsedExercises];
            newExercises.splice(index + 1, 0, duplicated);
            setParsedExercises(newExercises);
        }
    };

    const saveWorkout = async () => {
        const activeExercises = parsedExercises.filter(ex => !ex.isDeleted);
        if (activeExercises.length === 0) return;

        // Validate that all exercises have reps or seconds
        const invalidExercises = activeExercises.filter(ex => !ex.reps && !ex.seconds);
        if (invalidExercises.length > 0) {
            setError('Please enter reps or time for all exercises');
            return;
        }

        const newWorkout = {
            id: `workout-${Date.now()}`,
            date: new Date(),
            exercises: activeExercises.map(({ name, reps, weight, seconds, rounds, difficulty, station }) => ({
                name: name.toLowerCase(),
                reps: reps ? Number(reps) : null,
                weight: weight ? Number(weight) : null,
                seconds: seconds ? Number(seconds) : null,
                rounds: Number(rounds),
                difficulty: difficulty ? Number(difficulty) : null,
                station: station ? Number(station) : null,
            })),
        };

        try {
            setError('');
            const response = await authFetch('/api/workouts', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(newWorkout),
            });

            if (!response.ok) {
                throw new Error(`Request failed with status ${response.status}`);
            }

            const savedWorkout = await response.json();
            setWorkouts([{ ...savedWorkout, id: savedWorkout._id, date: new Date(savedWorkout.date) }, ...workouts]);
            localStorage.removeItem(CAPTURE_STORAGE_KEY);
            setImage(null);
            setParsedExercises([]);
            setShowManualForm(false);
            setSuccessMessage('Saved!');
            setSuccessFading(false);
            setTimeout(() => setSuccessFading(true), 1000);
            setTimeout(() => { setSuccessMessage(''); setSuccessFading(false); }, 2000);
        } catch (err) {
            console.error('Error saving workout:', err);
            setError('Failed to save workout. Please try again.');
        }
    };

    const handleManualAdd = () => {
        if (!manualEntry.name || (!manualEntry.reps && !manualEntry.seconds)) return;
        const newExercise = {
            id: Math.random(),
            name: manualEntry.name,
            reps: manualEntry.reps ? parseInt(manualEntry.reps, 10) : null,
            seconds: manualEntry.seconds ? parseInt(manualEntry.seconds, 10) : null,
            weight: manualEntry.weight ? parseInt(manualEntry.weight, 10) : '',
            rounds: manualEntry.sets ? parseInt(manualEntry.sets, 10) : 1,
            difficulty: manualEntry.difficulty ? parseInt(manualEntry.difficulty, 10) : '',
            station: manualEntry.station ? parseInt(manualEntry.station, 10) : '',
        };
        setParsedExercises([...parsedExercises, newExercise]);
        setManualEntry({ name: '', reps: '', weight: '', seconds: '', sets: '', difficulty: '', station: '' });
        setManualRecentEntries([]);
        setSuppressManualMatches(false);
        setShowManualForm(false);
    };

    const handleRetry = () => {
        console.log('[HCF] Clearing capture - image, exercises, localStorage');
        setImage(null);
        setError('');
        setParsedExercises([]);
        setShowManualForm(false);
        setManualEntry({ name: '', reps: '', weight: '', seconds: '', sets: '', difficulty: '', station: '' });
        setManualMatches([]);
        setManualRecentEntries([]);
        setSuppressManualMatches(false);

        localStorage.removeItem(CAPTURE_STORAGE_KEY);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        if (galleryInputRef.current) {
            galleryInputRef.current.value = '';
        }
        console.log('[HCF] Capture cleared - should show initial buttons');
    };

    const reprocessImage = () => {
        if (image) {
            parseImageWithAI(image.split(',')[1]);
        }
    };

    const applyMatchEntry = (index, match, entry, entryKey) => {
        if (entryKey) setUsedEntryKeys(prev => ({ ...prev, [index]: entryKey }));
        const currentEx = parsedExercises[index];
        const isNameChange = normalizeExerciseName(match.name) !== normalizeExerciseName(currentEx.name);
        if (isNameChange && !currentEx.originalData) {
            handleLogUpdate(index, 'originalData', {
                name: currentEx.name, weight: currentEx.weight, reps: currentEx.reps,
                seconds: currentEx.seconds, rounds: currentEx.rounds, difficulty: currentEx.difficulty,
            });
        }
        if (isNameChange) handleLogUpdate(index, 'name', displayExerciseName(match.name));
        if (entry.weight != null) handleLogUpdate(index, 'weight', entry.weight);
        if (entry.reps) handleLogUpdate(index, 'reps', entry.reps);
        if (entry.seconds) handleLogUpdate(index, 'seconds', entry.seconds);
        if (entry.rounds) handleLogUpdate(index, 'rounds', entry.rounds);
        if (entry.difficulty) handleLogUpdate(index, 'difficulty', entry.difficulty);
    };

    const handleRestoreOriginal = (index) => {
        const ex = parsedExercises[index];
        if (ex.originalData) {
            const updatedExercises = [...parsedExercises];
            updatedExercises[index] = {
                ...updatedExercises[index],
                ...ex.originalData,
                originalData: null,
            };
            setParsedExercises(updatedExercises);
            setUsedEntryKeys(prev => { const next = { ...prev }; delete next[index]; return next; });
        }
    };


    const handleManualPickExercise = (match) => {
        setManualEntry({ ...manualEntry, name: displayExerciseName(match.name) });
        setManualRecentEntries(match.entries);
        setManualMatches([]);
        setSuppressManualMatches(true);
    };

    const handleManualPickEntry = (entry) => {
        setManualEntry({
            ...manualEntry,
            weight: entry.weight || '',
            reps: entry.reps || '',
            seconds: entry.seconds || '',
            sets: entry.rounds || '1',
            difficulty: entry.difficulty || '',
        });
        setManualRecentEntries([]);
    };


    return (
        <div className="flex flex-col min-h-full">
            {successMessage && (
                <div className={`bg-green-600 text-white text-center py-3 rounded-lg mb-4 font-semibold transition-opacity duration-1000 ${successFading ? 'opacity-0' : 'opacity-100'}`}>
                    {successMessage}
                </div>
            )}
            {!image && (
                 <div className="flex flex-col items-center pt-8">
                    <button onClick={() => fileInputRef.current.click()} className={`${theme.accentBg} text-white font-bold py-4 px-6 rounded-lg flex items-center justify-center text-lg ${theme.accentHover} transition-transform transform hover:scale-105`}>
                        <IconCamera /> <span className="ml-2">Capture Station</span>
                    </button>
                    <input type="file" accept="image/*" capture="environment" ref={fileInputRef} onChange={handleImageChange} className="hidden" />
                    <p className={`${theme.textMuted} my-3`}>or</p>
                    <button onClick={() => galleryInputRef.current.click()} className={`${theme.inputBg} text-white font-bold py-3 px-6 rounded-lg flex items-center justify-center text-base ${theme.hoverBgLight} transition-colors`}>
                        <IconUpload /> <span className="ml-2">Upload Photo</span>
                    </button>
                    <input type="file" accept="image/*" ref={galleryInputRef} onChange={handleImageChange} className="hidden" />
                    <p className={`${theme.textMuted} mt-4 text-center text-sm`}>Take a photo of the station's screen or upload an existing one.</p>
                </div>
            )}

            {image && !isParsing && (
                <TransformWrapper
                    initialScale={1}
                    minScale={0.5}
                    maxScale={4}
                    centerOnInit={true}
                >
                    {({ zoomIn, zoomOut, resetTransform }) => (
                        <div className="mb-4 relative">
                            <div className="absolute top-2 left-2 z-10">
                                <button onClick={handleRetry} className="bg-black/60 p-2 rounded-full text-white hover:bg-black/80"><IconX className="w-5 h-5" /></button>
                            </div>
                            <div className="absolute top-2 right-2 z-10 flex gap-2">
                                <button onClick={() => zoomIn()} className="bg-gray-800/70 p-2 rounded-full text-white hover:bg-gray-700"><IconZoomIn className="w-5 h-5" /></button>
                                <button onClick={() => zoomOut()} className="bg-gray-800/70 p-2 rounded-full text-white hover:bg-gray-700"><IconZoomOut className="w-5 h-5" /></button>
                                <button onClick={() => resetTransform()} className="bg-gray-800/70 p-2 rounded-full text-white hover:bg-gray-700"><IconRefreshCw className="w-5 h-5" /></button>
                            </div>
                            <div className={`rounded-lg overflow-hidden border-2 ${theme.border}`} style={{ maxHeight: '50vh' }}>
                                <TransformComponent
                                    wrapperStyle={{ width: '100%', maxHeight: '50vh' }}
                                    contentStyle={{ display: 'flex', justifyContent: 'center' }}
                                >
                                    <img src={image} alt="Uploaded workout" style={{ maxHeight: '50vh', width: 'auto', objectFit: 'contain' }} />
                                </TransformComponent>
                            </div>
                        </div>
                    )}
                </TransformWrapper>
            )}

            {isParsing && <LoadingSpinner text="Analyzing image..." settings={settings} />}
            
            {error && (
                <div className="bg-yellow-900/50 p-4 rounded-lg text-center space-y-4 my-4">
                    <p className="text-yellow-300">{error}</p>
                    <button onClick={handleRetry} className={`${theme.accentBg} text-white font-bold py-2 px-5 rounded-lg ${theme.accentHover} transition-colors`}>
                        Try Again
                    </button>
                </div>
            )}

            <div className="mt-4">
                <button onClick={() => setShowManualForm(!showManualForm)} className={`w-full text-center ${theme.accentText} font-semibold py-2`}>
                    {showManualForm ? 'Cancel Manual Entry' : 'Add Exercise Manually'}
                </button>
                {showManualForm && (
                    <div className={`${theme.cardBg} p-4 rounded-lg mt-2 space-y-3`}>
                        <div className="relative">
                            <input type="text" placeholder="Exercise Name" value={manualEntry.name} onChange={e => { setManualEntry({...manualEntry, name: e.target.value}); setManualRecentEntries([]); setSuppressManualMatches(false); setShowAllManualMatches(false); }} className={`w-full ${theme.inputBg} p-2 rounded-md`} />
                            {manualMatches.length > 0 && manualRecentEntries.length === 0 && (() => {
                                const INITIAL = 3;
                                const visible = showAllManualMatches ? manualMatches : manualMatches.slice(0, INITIAL);
                                const hidden = manualMatches.length - visible.length;
                                return (
                                <div className={`mt-1 ${theme.cardBg} rounded-lg border ${theme.border} shadow-lg max-h-72 overflow-y-auto`}>
                                    <div className={`flex items-center justify-between px-3 py-1.5 border-b ${theme.border} ${theme.inputBg}`}>
                                        <span className={`text-xs ${theme.textMuted}`}>{manualMatches.length} match{manualMatches.length === 1 ? '' : 'es'}</span>
                                        <button
                                            type="button"
                                            onClick={() => setSuppressManualMatches(true)}
                                            className={`text-xs ${theme.textMuted} hover:${theme.textPrimary} px-1`}
                                            aria-label="Dismiss matches"
                                        >
                                            ✕
                                        </button>
                                    </div>
                                    {visible.map((match, i) => (
                                        <button
                                            key={i}
                                            type="button"
                                            className={`w-full text-left px-3 py-2 ${i > 0 ? `border-t ${theme.border}` : ''}`}
                                            onClick={() => handleManualPickExercise(match)}
                                        >
                                            <span className={`block text-sm font-medium ${theme.textPrimary}`}>{displayExerciseName(match.name)}</span>
                                            <span className={`block text-xs ${theme.textMuted}`}>{match.entries.length} log{match.entries.length === 1 ? '' : 's'} • last {match.lastDate}</span>
                                        </button>
                                    ))}
                                    {hidden > 0 && (
                                        <button
                                            type="button"
                                            onClick={() => setShowAllManualMatches(true)}
                                            className={`w-full text-left px-3 py-2 border-t ${theme.border} text-xs ${theme.textMuted} hover:${theme.textPrimary}`}
                                        >
                                            Show all {manualMatches.length} matches ({hidden} more)
                                        </button>
                                    )}
                                </div>
                                );
                            })()}
                        </div>
                        {manualRecentEntries.length > 0 && (
                            <>
                                <div className="flex flex-wrap gap-2">
                                    {manualRecentEntries.map((entry, i) => {
                                        const label = entry.seconds
                                            ? `${entry.rounds || 1}x ${entry.seconds}s`
                                            : `${entry.reps || 0}${entry.weight ? ` @ ${entry.weight}lbs` : ' BW'}`;
                                        const isRecent = i === 0;
                                        return (
                                            <button
                                                key={i}
                                                onClick={() => handleManualPickEntry(entry)}
                                                className={`text-xs px-3 py-2 rounded-lg flex flex-col items-start gap-0.5 transition-colors border ${
                                                    isRecent
                                                        ? `${theme.accentBg} text-white border-transparent ${theme.accentHover}`
                                                        : `${theme.inputBg} ${theme.textPrimary} ${theme.border} ${theme.hoverBg}`
                                                }`}
                                            >
                                                {isRecent && (
                                                    <span className="text-[10px] uppercase tracking-wide font-semibold opacity-80">Recent</span>
                                                )}
                                                <span className="font-semibold">{label}</span>
                                                <span className={`text-[10px] ${isRecent ? 'opacity-80' : theme.textMuted}`}>{entry.date}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => onViewExerciseHistory(manualEntry.name, 'capture')}
                                    className={`text-xs ${theme.accentText} underline hover:no-underline`}
                                >
                                    View History
                                </button>
                            </>
                        )}
                        <div className="grid grid-cols-2 gap-2">
                            <input type="number" placeholder="Reps" value={manualEntry.reps} onChange={e => setManualEntry({...manualEntry, reps: e.target.value, seconds: ''})} className={`w-full ${theme.inputBg} p-2 rounded-md`} />
                            <input type="number" placeholder="OR Seconds" value={manualEntry.seconds} onChange={e => setManualEntry({...manualEntry, seconds: e.target.value, reps: ''})} className={`w-full ${theme.inputBg} p-2 rounded-md`} />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <input type="number" placeholder="Sets" value={manualEntry.sets} onChange={e => setManualEntry({...manualEntry, sets: e.target.value})} className={`w-full ${theme.inputBg} p-2 rounded-md`} />
                            <input type="number" placeholder="Weight (optional)" value={manualEntry.weight} onChange={e => setManualEntry({...manualEntry, weight: e.target.value})} className={`w-full ${theme.inputBg} p-2 rounded-md`} />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <input type="number" placeholder="Station #" value={manualEntry.station} onChange={e => setManualEntry({...manualEntry, station: e.target.value})} className={`w-full ${theme.inputBg} p-2 rounded-md`} />
                            <select value={manualEntry.difficulty} onChange={e => setManualEntry({...manualEntry, difficulty: e.target.value})} className={`w-full ${theme.inputBg} p-2 rounded-md`}>
                                <option value="">Difficulty</option>
                                <option value="1">🔥 Easy</option>
                                <option value="2">🔥🔥 Moderate</option>
                                <option value="3">🔥🔥🔥 Hard</option>
                                <option value="4">🔥🔥🔥🔥 Brutal</option>
                            </select>
                        </div>
                        <button onClick={handleManualAdd} className={`w-full ${theme.accentBg} text-white font-bold py-2 rounded-lg ${theme.accentHover} transition-colors`}>Add Exercise</button>
                    </div>
                )}
            </div>

            {parsedExercises.length > 0 && (
                <div className="flex-grow pb-20">
                    <h2 className="text-xl font-semibold mb-4">Log Your Sets</h2>
                    <div className="space-y-4 mb-4">
                        {parsedExercises.map((ex, index) => (
                            ex.isDeleted ? (
                                <div key={ex.id} className={`${theme.cardBg} opacity-50 p-3 rounded-lg flex justify-between items-center`}>
                                    <span className={`${theme.textFaint} capitalize`}>{ex.name}</span>
                                    <button onClick={() => handleRestoreExercise(ex.id)} className="text-sm text-blue-400 hover:text-blue-300 font-medium">
                                        Restore
                                    </button>
                                </div>
                            ) : (
                            <div key={ex.id} className={`${theme.cardBg} p-4 rounded-lg`}>
                                <div className="flex justify-between items-center mb-1">
                                    <input
                                        type="text"
                                        value={ex.name}
                                        onChange={(e) => handleLogUpdate(index, 'name', e.target.value)}
                                        className={`text-lg font-bold capitalize bg-transparent border-b-2 ${theme.border} focus:border-white outline-none w-full`}
                                    />
                                    <div className="flex">
                                        <button onClick={() => handleDuplicateExercise(ex.id)} className={`p-2 rounded-full ${theme.hoverBg}`} title="Duplicate">
                                            <IconCopy className={`w-5 h-5 ${theme.textFaint}`} />
                                        </button>
                                        <button onClick={() => handleDeleteParsedExercise(ex.id)} className={`p-2 -mr-2 rounded-full ${theme.hoverBg}`} title="Delete">
                                            <IconX className={`w-5 h-5 ${theme.textFaint}`} />
                                        </button>
                                    </div>
                                </div>
                                {ex.originalData && (
                                    <p className={`text-xs ${theme.textFaint} mb-1`}>
                                        Was: "{displayExerciseName(ex.originalData.name)}" — <button onClick={() => handleRestoreOriginal(index)} className="text-blue-400 hover:text-blue-300">Restore</button>
                                    </p>
                                )}
                                <MatchHistory
                                    matches={ex.topMatches}
                                    onUseEntry={(match, entry, key) => applyMatchEntry(index, match, entry, key)}
                                    usedEntryKey={usedEntryKeys[index]}
                                    settings={settings}
                                />
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="space-y-1">
                                        <label className={`text-sm ${theme.textMuted}`}>Reps</label>
                                        <input type="number" value={ex.reps || ''} onChange={(e) => handleLogUpdate(index, 'reps', e.target.value)} className={`w-full ${theme.inputBg} p-2 rounded-md`} />
                                    </div>
                                    <div className="space-y-1">
                                        <label className={`text-sm ${theme.textMuted}`}>OR Seconds</label>
                                        <input type="number" value={ex.seconds || ''} onChange={(e) => handleLogUpdate(index, 'seconds', e.target.value)} className={`w-full ${theme.inputBg} p-2 rounded-md`} />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-3 mt-3">
                                    <div className="space-y-1">
                                        <label className={`text-sm ${theme.textMuted}`}>Sets</label>
                                        <input type="number" value={ex.rounds} onChange={(e) => handleLogUpdate(index, 'rounds', e.target.value)} className={`w-full ${theme.inputBg} p-2 rounded-md`} />
                                    </div>
                                    <div className="space-y-1">
                                        <label className={`text-sm ${theme.textMuted}`}>Weight</label>
                                        <input type="number" placeholder="BW" value={ex.weight} onChange={(e) => handleLogUpdate(index, 'weight', e.target.value)} className={`w-full ${theme.inputBg} p-2 rounded-md`} />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-3 mt-3">
                                    <div className="space-y-1">
                                        <label className={`text-sm ${theme.textMuted}`}>Station #</label>
                                        <input type="number" placeholder="—" value={ex.station} onChange={(e) => handleLogUpdate(index, 'station', e.target.value)} className={`w-full ${theme.inputBg} p-2 rounded-md`} />
                                    </div>
                                    <div className="space-y-1">
                                        <label className={`text-sm ${theme.textMuted}`}>Difficulty</label>
                                        <select value={ex.difficulty} onChange={(e) => handleLogUpdate(index, 'difficulty', e.target.value)} className={`w-full ${theme.inputBg} p-2 rounded-md`}>
                                            <option value="">—</option>
                                            <option value="1">🔥 Easy</option>
                                            <option value="2">🔥🔥 Moderate</option>
                                            <option value="3">🔥🔥🔥 Hard</option>
                                            <option value="4">🔥🔥🔥🔥 Brutal</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                            )
                        ))}
                    </div>
                    {parsedExercises.every(ex => ex.isDeleted) ? (
                        <div className="text-center py-4 space-y-3">
                            <p className={theme.textMuted}>All exercises removed.</p>
                            <button onClick={handleRestoreAll} className="w-full bg-yellow-600 text-white font-bold py-3 rounded-lg hover:bg-yellow-700 transition-colors">
                                Restore All
                            </button>
                            <button onClick={reprocessImage} className="w-full bg-blue-600 text-white font-bold py-3 rounded-lg hover:bg-blue-700 transition-colors">
                                Re-process Image
                            </button>
                            <button onClick={handleRetry} className={`w-full text-center ${theme.accentText} font-semibold py-2`}>
                                Start Over
                            </button>
                        </div>
                    ) : (
                        <>
                            {parsedExercises.some(ex => ex.isDeleted) && (
                                <button onClick={handleRestoreAll} className="w-full bg-yellow-600 text-white font-semibold py-2 rounded-lg hover:bg-yellow-700 transition-colors mb-2">
                                    Restore All Deleted
                                </button>
                            )}
                            <button onClick={saveWorkout} className="w-full bg-green-600 text-white font-bold py-3 rounded-lg hover:bg-green-700 transition-colors">Save Workout</button>
                            <button onClick={handleRetry} className={`w-full text-center ${theme.accentText} font-semibold py-2 mt-2`}>Cancel</button>
                        </>
                    )}
                </div>
            )}

        </div>
    );
}

const CustomTooltip = ({ active, payload, label, settings }) => {
  const theme = getTheme(settings?.theme);
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const weightUnit = settings?.weightUnit || 'lbs';
    const fmtWeight = (w) => w ? (weightUnit === 'kg' ? `${Math.round(w * 0.453592)}kg` : `${w}lbs`) : 'BW';
    const fmtVolume = (v) => weightUnit === 'kg' ? Math.round(v * 0.453592) : v;

    return (
      <div className={`${theme.inputBg} p-3 rounded-lg border ${theme.border} shadow-lg`}>
        <p className={`label ${theme.textSecondary}`}>{`${label}`}</p>
        {payload.map((pld, index) => {
          let displayValue = pld.value;
          if (pld.dataKey === 'weight') {
            displayValue = pld.value === 0 ? 'BW' : fmtWeight(pld.value);
          }
          return <p key={index} style={{ color: pld.color }} className="intro">{`${pld.name}: ${displayValue}`}</p>;
        })}
        {data.volume > 0 && (
             <p className="text-yellow-400">{`Volume: ${fmtVolume(data.volume).toLocaleString()} ${weightUnit}`}</p>
        )}
      </div>
    );
  }
  return null;
};

function AnalyticsScreen({ workouts, selectedExercise, setSelectedExercise, onNavigateToDate, settings, origin, onBackToCapture }) {
    const [chartData, setChartData] = useState([]);
    const [rawEntries, setRawEntries] = useState([]);
    const [allExerciseNames, setAllExerciseNames] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");

    // Weight formatting & theme
    const weightUnit = settings?.weightUnit || 'lbs';
    const fmtWeight = (w) => w ? (weightUnit === 'kg' ? `${Math.round(w * 0.453592)}kg` : `${w}lbs`) : 'BW';
    const theme = getTheme(settings?.theme);

    useEffect(() => {
        const names = new Set();
        workouts.forEach(w => w.exercises.forEach(ex => {
            // Normalize name for deduplication
            const normalized = normalizeExerciseName(ex.name);
            names.add(normalized);
        }));
        setAllExerciseNames(Array.from(names).sort());
    }, [workouts]);

    useEffect(() => {
        if (selectedExercise) {
            const dataByDate = {};
            const entries = [];
            const sortedWorkouts = [...workouts].sort((a, b) => new Date(a.date) - new Date(b.date));
            const normalizedSelected = normalizeExerciseName(selectedExercise);

            sortedWorkouts.forEach(w => {
                w.exercises.forEach(ex => {
                    const normalizedName = normalizeExerciseName(ex.name);
                    if (normalizedName === normalizedSelected) {
                        const dateKey = new Date(w.date).toLocaleDateString('en-CA');

                        // Collect raw entries for the list
                        entries.push({
                            date: dateKey,
                            dateFormatted: new Date(w.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                            reps: ex.reps,
                            weight: ex.weight,
                            seconds: ex.seconds,
                            rounds: ex.rounds || 1,
                            difficulty: ex.difficulty,
                            station: ex.station,
                            volume: (ex.reps || 0) * (ex.rounds || 1) * (ex.weight || 0)
                        });

                        // Aggregate by date for chart
                        if (!dataByDate[dateKey]) {
                            dataByDate[dateKey] = {
                                date: dateKey,
                                totalReps: 0,
                                maxWeight: 0,
                                totalSeconds: 0,
                                volume: 0,
                                count: 0,
                                hasSeconds: false
                            };
                        }
                        const entry = dataByDate[dateKey];
                        entry.count++;
                        entry.totalReps += (ex.reps || 0) * (ex.rounds || 1);
                        entry.totalSeconds += (ex.seconds || 0) * (ex.rounds || 1);
                        entry.maxWeight = Math.max(entry.maxWeight, ex.weight || 0);
                        entry.volume += (ex.reps || 0) * (ex.rounds || 1) * (ex.weight || 0);
                        if (ex.seconds) entry.hasSeconds = true;
                    }
                });
            });

            // Convert to array for chart
            const data = Object.values(dataByDate).map(entry => ({
                date: entry.date,
                reps: entry.totalReps,
                weight: entry.maxWeight,
                seconds: entry.totalSeconds,
                volume: entry.volume,
                isTimedExercise: entry.hasSeconds && entry.totalSeconds > 0
            }));

            setChartData(data);
            setRawEntries(entries.reverse()); // Most recent first
        } else {
            setChartData([]);
            setRawEntries([]);
        }
    }, [selectedExercise, workouts]);

    const filteredExercises = allExerciseNames.filter(name =>
        name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const isTimedExercise = chartData.length > 0 && chartData[0].isTimedExercise;

    return (
        <div>
            <div className="mb-4">
                <div className="relative">
                    <input
                        type="text"
                        placeholder="Search exercises..."
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        className={`w-full p-3 pl-10 rounded-md ${theme.inputBg}`}
                    />
                    <IconSearch className={`absolute left-3 top-1/2 -translate-y-1/2 ${theme.textMuted}`} />
                </div>
            </div>

            {!selectedExercise && (
                <div className="space-y-2 mb-6">
                    {filteredExercises.length === 0 ? (
                        <p className={`${theme.textFaint} text-center py-4`}>No exercises found</p>
                    ) : (
                        filteredExercises.map(name => (
                            <button
                                key={name}
                                onClick={() => setSelectedExercise(name)}
                                className={`w-full text-left p-3 rounded-lg transition-colors ${theme.cardBg} ${theme.hoverBg}`}
                            >
                                {displayExerciseName(name)}
                            </button>
                        ))
                    )}
                </div>
            )}

            {selectedExercise && (
                <>
                    <button
                        onClick={origin === 'capture' ? onBackToCapture : () => setSelectedExercise(null)}
                        className={`mb-4 ${theme.accentText} font-semibold flex items-center gap-1`}
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        Back
                    </button>
                    <div className={`p-4 rounded-lg ${theme.cardBg}`}>
                        <h2 className="text-xl font-bold mb-4">{displayExerciseName(selectedExercise)} Progress</h2>
                        <div style={{ width: '100%', height: 300 }}>
                            <ResponsiveContainer>
                                <LineChart data={chartData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke={settings?.theme === 'light' ? "#E2E8F0" : "#4A5568"} />
                                    <XAxis dataKey="date" stroke={settings?.theme === 'light' ? "#4A5568" : "#A0AEC0"} />
                                    <YAxis yAxisId="left" stroke={theme.chartSecondary} />
                                    <YAxis yAxisId="right" orientation="right" stroke={theme.chartAccent} tickFormatter={(value) => value === 0 ? 'BW' : value} />
                                    <Tooltip content={<CustomTooltip settings={settings} />} />
                                    <Legend />
                                    {isTimedExercise ? (
                                        <>
                                            <Line yAxisId="left" type="monotone" dataKey="seconds" stroke={theme.chartSecondary} name="Duration (s)" />
                                            <Line yAxisId="right" type="monotone" dataKey="weight" stroke={theme.chartAccent} name={`Weight (${weightUnit})`} />
                                        </>
                                    ) : (
                                        <>
                                            <Line yAxisId="left" type="monotone" dataKey="reps" stroke={theme.chartSecondary} name="Total Reps" />
                                            <Line yAxisId="right" type="monotone" dataKey="weight" stroke={theme.chartAccent} name={`Max Weight (${weightUnit})`} />
                                        </>
                                    )}
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {rawEntries.length > 0 && (
                        <div className={`p-4 rounded-lg mt-4 ${theme.cardBg}`}>
                            <h3 className="text-lg font-semibold mb-3">All Entries ({rawEntries.length})</h3>
                            <div className="space-y-2 max-h-64 overflow-y-auto">
                                {rawEntries.map((entry, idx) => (
                                    <div
                                        key={idx}
                                        className={`flex justify-between items-center p-3 rounded-md text-sm cursor-pointer transition-colors ${theme.inputBg} ${theme.hoverBgLight}`}
                                        onClick={() => onNavigateToDate(entry.date)}
                                    >
                                        <span className={theme.textMuted}>{entry.dateFormatted}</span>
                                        <span>
                                            {entry.seconds ? (
                                                <>{entry.rounds}x {entry.seconds}s</>
                                            ) : (
                                                <>{entry.reps} {entry.reps === 1 ? 'rep' : 'reps'} x {entry.rounds} {entry.rounds === 1 ? 'set' : 'sets'}</>
                                            )}
                                            {entry.weight ? <> @ {fmtWeight(entry.weight)}</> : ' (BW)'}
                                        </span>
                                        {entry.difficulty && (
                                            <span>{'🔥'.repeat(entry.difficulty)}</span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

// --- Components ---

function BottomNav({ activePage, setPage, settings }) {
    const theme = getTheme(settings?.theme);
    const navItems = [
        { name: 'home', icon: IconHome, label: 'Home' },
        { name: 'capture', icon: IconCamera, label: 'Capture' },
        { name: 'history', icon: IconHistory, label: 'History' },
        { name: 'analytics', icon: IconBarChart2, label: 'Analytics' },
    ];

    return (
        <nav className={`fixed bottom-0 left-0 right-0 md:left-1/2 md:-translate-x-1/2 md:max-w-lg ${theme.navBg} border-t ${theme.navBorder} flex justify-around`}>
            {navItems.map(item => (
                <button
                    key={item.name}
                    onClick={() => setPage(item.name)}
                    className={`flex flex-col items-center justify-center w-full pt-3 pb-2 transition-colors ${activePage === item.name ? theme.navActive : `${theme.textMuted} hover:text-white`}`}
                >
                    <item.icon className="w-6 h-6" />
                    <span className="text-xs mt-1">{item.label}</span>
                </button>
            ))}
        </nav>
    );
}

function LoadingSpinner({ text = 'Loading...', settings }) {
    const theme = getTheme(settings?.theme);
    return (
        <div className="flex flex-col items-center justify-center h-full">
            <svg className={`animate-spin -ml-1 mr-3 h-10 w-10 ${theme.accentText}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p className="mt-4 text-lg">{text}</p>
        </div>
    );
}

// --- Auth Wrapper ---
function AuthGate() {
    const [authed, setAuthed] = useState(false);
    const [checking, setChecking] = useState(true);

    useEffect(() => {
        async function checkAuth() {
            const params = new URLSearchParams(window.location.search);
            const token = params.get('token');

            if (token) {
                try {
                    const res = await fetch(apiUrl('/api/auth/verify'), {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ token }),
                    });
                    const data = await res.json();
                    if (res.ok && data.sessionToken) {
                        setSessionToken(data.sessionToken);
                        window.history.replaceState({}, '', window.location.pathname);
                        setAuthed(true);
                        setChecking(false);
                        return;
                    }
                } catch {
                    // Fall through to check existing session
                }
            }

            const existing = getSessionToken();
            if (existing) {
                const payload = decodeToken(existing);
                if (payload) {
                    setAuthed(true);
                } else {
                    clearSessionToken();
                }
            }

            setChecking(false);
        }
        checkAuth();
    }, []);

    if (checking) {
        return (
            <div className="min-h-screen bg-gray-900 flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500" />
            </div>
        );
    }

    if (!authed) {
        return <LandingPage />;
    }

    return <AppContent onSignOut={() => { clearSessionToken(); setAuthed(false); }} />;
}

export default function App() {
    return <AuthGate />;
}
